// frontend/src/pages/POS/POS.js - COMPLETE WITH FIXED BULK SALE SERIAL SELECTION
import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Card,
  CardContent,
  InputAdornment,
  MenuItem,
  Chip,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Badge,
  Autocomplete,
  Modal,
  Fade,
  Backdrop,
  FormControlLabel,
  Checkbox,
  Tooltip,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel
} from '@mui/material';
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Receipt as ReceiptIcon,
  Print as PrintIcon,
  Refresh as RefreshIcon,
  Cancel as CancelIcon,
  ArrowBack as ArrowBackIcon,
  LocalOffer as LocalOfferIcon,
  Payment as PaymentIcon,
  Calculate as CalculateIcon,
  PersonAdd as PersonAddIcon,
  Edit as EditIcon,
  Close as CloseIcon,
  Save as SaveIcon,
  Info as InfoIcon,
  CreditCard as CreditCardIcon,
  Warning as WarningIcon,
  Block as InactiveIcon,
  AccountBalance as ChequeIcon,
  Verified as WarrantyIcon,
  ConfirmationNumber as SerialIcon,
  CheckCircle as PaidIcon,
  Pending as PendingIcon,
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { 
  getProducts, 
  getPOSStats, 
  createPOS,
  getCustomers,
  createCustomer,
  getProductSerials,
  reserveSerials
} from '../../services/api';
import './POS.css';

// Helper function to format currency
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-LK', {
    style: 'currency',
    currency: 'LKR',
    minimumFractionDigits: 2
  }).format(amount || 0);
};

// ============ COMPLETE FIXED Serial Number Selection Modal with Working Multi-Select ============
const SerialSelectionModal = ({ 
  open, 
  onClose, 
  product, 
  onSelect,
  quantity 
}) => {
  const [serials, setSerials] = useState([]);
  const [selectedSerials, setSelectedSerials] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open && product) {
      fetchAvailableSerials();
    } else {
      setSelectedSerials([]);
      setError(null);
    }
  }, [open, product, quantity]);

  const fetchAvailableSerials = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getProductSerials(product.id, { status: 'available' });
      
      if (response && response.success) {
        const availableSerials = response.data || [];
        setSerials(availableSerials);
        
        if (availableSerials.length < quantity) {
          setError(`Only ${availableSerials.length} good serials available, but you need ${quantity}`);
        }
      }
    } catch (err) {
      setError(err.message || 'Failed to load serial numbers');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleSerial = (serial) => {
    setSelectedSerials(prev => {
      const isSelected = prev.some(s => s.id === serial.id);
      
      if (isSelected) {
        return prev.filter(s => s.id !== serial.id);
      } else {
        if (prev.length < quantity) {
          return [...prev, serial];
        } else {
          setError(`You can only select ${quantity} serial numbers`);
          setTimeout(() => setError(null), 3000);
          return prev;
        }
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedSerials.length === serials.length && selectedSerials.length === Math.min(serials.length, quantity)) {
      setSelectedSerials([]);
    } else {
      const maxToSelect = Math.min(serials.length, quantity);
      const serialsToSelect = serials.slice(0, maxToSelect);
      setSelectedSerials(serialsToSelect);
    }
  };

  const handleConfirm = () => {
    if (selectedSerials.length === quantity) {
      onSelect(selectedSerials);
      onClose();
    } else {
      setError(`Please select exactly ${quantity} serial numbers. Currently selected: ${selectedSerials.length}`);
      setTimeout(() => setError(null), 3000);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <SerialIcon color="primary" />
          <Typography variant="h6">
            Select Serial Numbers for {product?.name}
          </Typography>
        </Box>
        <Typography variant="subtitle2" color="text.secondary">
          Please select exactly {quantity} serial number(s) from {serials.length} available
        </Typography>
      </DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2">
            Selected: <strong>{selectedSerials.length}</strong> of {quantity}
          </Typography>
          <Button 
            size="small" 
            onClick={handleSelectAll}
            disabled={serials.length === 0}
          >
            {selectedSerials.length === Math.min(serials.length, quantity) ? 'Deselect All' : `Select First ${Math.min(serials.length, quantity)}`}
          </Button>
        </Box>

        {loading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : serials.length === 0 ? (
          <Alert severity="warning">
            No available good serial numbers found for this product.
          </Alert>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={selectedSerials.length > 0 && selectedSerials.length < Math.min(serials.length, quantity)}
                      checked={selectedSerials.length === Math.min(serials.length, quantity) && serials.length > 0}
                      onChange={handleSelectAll}
                    />
                  </TableCell>
                  <TableCell>Serial Number</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell>Warranty Expiry</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {serials.map((serial) => {
                  const isSelected = selectedSerials.some(s => s.id === serial.id);
                  const isDisabled = !isSelected && selectedSerials.length >= quantity;
                  
                  return (
                    <TableRow 
                      key={serial.id}
                      hover
                      onClick={() => !isDisabled && handleToggleSerial(serial)}
                      sx={{ 
                        cursor: isDisabled ? 'not-allowed' : 'pointer',
                        backgroundColor: isSelected ? '#e3f2fd' : 'inherit',
                        opacity: isDisabled ? 0.5 : 1
                      }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={isSelected}
                          disabled={isDisabled}
                          onChange={() => handleToggleSerial(serial)}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={<SerialIcon fontSize="small" />}
                          label={serial.serial_number}
                          size="small"
                          color="success"
                          variant="outlined"
                          sx={{ fontFamily: 'monospace' }}
                        />
                      </TableCell>
                      <TableCell>{serial.current_location || 'Main Warehouse'}</TableCell>
                      <TableCell>
                        {serial.warranty_expiry ? new Date(serial.warranty_expiry).toLocaleDateString() : 'N/A'}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button 
          onClick={handleConfirm} 
          variant="contained" 
          disabled={selectedSerials.length !== quantity}
          color="primary"
        >
          Confirm Selection ({selectedSerials.length}/{quantity})
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ============ FIXED: Bulk Serial Selection Modal ============
const BulkSerialSelectionModal = ({ 
  open, 
  onClose, 
  product, 
  onSelect,
  quantity 
}) => {
  const [serials, setSerials] = useState([]);
  const [selectedSerials, setSelectedSerials] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open && product) {
      fetchAvailableSerials();
    } else {
      setSelectedSerials([]);
      setError(null);
    }
  }, [open, product, quantity]);

  const fetchAvailableSerials = async () => {
    try {
      setLoading(true);
      const response = await getProductSerials(product.id, { status: 'available' });
      
      if (response && response.success) {
        const availableSerials = response.data || [];
        setSerials(availableSerials);
        
        if (availableSerials.length < quantity) {
          setError(`Only ${availableSerials.length} good serials available for this bulk sale, but you need ${quantity}`);
        }
      }
    } catch (err) {
      setError(err.message || 'Failed to load serial numbers');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleSerial = (serial) => {
    setSelectedSerials(prev => {
      const isSelected = prev.some(s => s.id === serial.id);
      
      if (isSelected) {
        return prev.filter(s => s.id !== serial.id);
      } else {
        if (prev.length < quantity) {
          return [...prev, serial];
        } else {
          setError(`You can only select ${quantity} serial numbers for this product`);
          setTimeout(() => setError(null), 3000);
          return prev;
        }
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedSerials.length === serials.length && selectedSerials.length === Math.min(serials.length, quantity)) {
      setSelectedSerials([]);
    } else {
      const maxToSelect = Math.min(serials.length, quantity);
      const serialsToSelect = serials.slice(0, maxToSelect);
      setSelectedSerials(serialsToSelect);
    }
  };

  const handleConfirm = () => {
    if (selectedSerials.length === quantity) {
      onSelect(selectedSerials);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <SerialIcon color="secondary" />
          <Typography variant="h6">
            Select Serial Numbers for Bulk Sale: {product?.name}
          </Typography>
        </Box>
        <Typography variant="subtitle2" color="text.secondary">
          Please select {quantity} serial number(s) for your bulk package
        </Typography>
      </DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2">
            Selected: <strong>{selectedSerials.length}</strong> of {quantity}
          </Typography>
          <Button 
            size="small" 
            onClick={handleSelectAll}
            disabled={serials.length === 0}
          >
            {selectedSerials.length === Math.min(serials.length, quantity) ? 'Deselect All' : `Select First ${Math.min(serials.length, quantity)}`}
          </Button>
        </Box>

        {loading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : serials.length === 0 ? (
          <Alert severity="warning">
            No available good serial numbers found for this product.
          </Alert>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={selectedSerials.length > 0 && selectedSerials.length < Math.min(serials.length, quantity)}
                      checked={selectedSerials.length === Math.min(serials.length, quantity) && serials.length > 0}
                      onChange={handleSelectAll}
                    />
                  </TableCell>
                  <TableCell>Serial Number</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell>Warranty Expiry</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {serials.map((serial) => {
                  const isSelected = selectedSerials.some(s => s.id === serial.id);
                  const isDisabled = !isSelected && selectedSerials.length >= quantity;
                  
                  return (
                    <TableRow 
                      key={serial.id}
                      hover
                      onClick={() => !isDisabled && handleToggleSerial(serial)}
                      sx={{ 
                        cursor: isDisabled ? 'not-allowed' : 'pointer',
                        backgroundColor: isSelected ? '#fff3e0' : 'inherit',
                        opacity: isDisabled ? 0.5 : 1
                      }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={isSelected}
                          disabled={isDisabled && !isSelected}
                          onChange={() => handleToggleSerial(serial)}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={<SerialIcon fontSize="small" />}
                          label={serial.serial_number}
                          size="small"
                          color="secondary"
                          variant="outlined"
                          sx={{ fontFamily: 'monospace' }}
                        />
                      </TableCell>
                      <TableCell>{serial.current_location || 'Main Warehouse'}</TableCell>
                      <TableCell>
                        {serial.warranty_expiry ? new Date(serial.warranty_expiry).toLocaleDateString() : 'N/A'}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button 
          onClick={handleConfirm} 
          variant="contained" 
          disabled={selectedSerials.length !== quantity}
          color="secondary"
        >
          Confirm Selection ({selectedSerials.length}/{quantity})
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ============ Generate unique invoice number ============
const generateInvoiceNumber = () => {
  const now = new Date();
  const dateStr = now.toISOString().slice(0, 10).replace(/-/g, '');
  
  const storageKey = `pos_invoice_counter_${dateStr}`;
  let counter = parseInt(localStorage.getItem(storageKey)) || 0;
  
  counter++;
  localStorage.setItem(storageKey, counter);
  
  const sequence = counter.toString().padStart(3, '0');
  const invoiceNumber = `INV-${dateStr}-${sequence}`;
  localStorage.setItem('last_invoice_number', invoiceNumber);
  
  return invoiceNumber;
};

// ============ Credit Payment Modal ============
const CreditPaymentModal = ({ 
  open, 
  onClose, 
  customer, 
  amount, 
  onSuccess,
  onCreateCreditSale,
  invoiceNumber
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  
  const [paymentData, setPaymentData] = useState({
    dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    description: ''
  });

  useEffect(() => {
    if (open) {
      setPaymentData({
        dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        description: ''
      });
      setError(null);
      setSuccess(false);
    }
  }, [open]);

  const handleCreditSale = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await onCreateCreditSale(customer, paymentData, amount, invoiceNumber);
      
      if (response.success) {
        setSuccess(true);
        setTimeout(() => {
          onSuccess(response.data, invoiceNumber, paymentData.dueDate);
          onClose();
        }, 1500);
      }
    } catch (err) {
      setError(err.message || 'Failed to process credit sale');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <CreditCardIcon color="primary" />
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Process Credit Payment
          </Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Credit sale processed successfully!
          </Alert>
        )}
        
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            Customer: <strong>{customer.name}</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Code: {customer.customer_code || 'N/A'}
          </Typography>
          <Typography variant="body2" color="primary" gutterBottom>
            Invoice #: <strong>{invoiceNumber}</strong>
          </Typography>
          
          <Box sx={{ my: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
            <Typography variant="h6" align="center" sx={{ fontWeight: 'bold', color: '#e74c3c' }}>
              Amount: {formatCurrency(amount)}
            </Typography>
          </Box>
          
          <TextField
            fullWidth
            label="Due Date"
            type="date"
            value={paymentData.dueDate}
            onChange={(e) => setPaymentData({...paymentData, dueDate: e.target.value})}
            margin="normal"
            InputLabelProps={{ shrink: true }}
            disabled={loading || success}
          />
          
          <TextField
            fullWidth
            label="Description (Optional)"
            value={paymentData.description}
            onChange={(e) => setPaymentData({...paymentData, description: e.target.value})}
            margin="normal"
            multiline
            rows={2}
            disabled={loading || success}
            placeholder="e.g., POS transaction for invoice"
          />
          
          {customer.available_credit !== undefined && customer.available_credit < amount && (
            <Alert severity="warning" sx={{ mt: 2 }} icon={<WarningIcon />}>
              <Typography variant="body2">
                Customer's available credit ({formatCurrency(customer.available_credit)}) is less than the transaction amount.
              </Typography>
            </Alert>
          )}
        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} color="inherit" disabled={loading}>
          Cancel
        </Button>
        <Button 
          onClick={handleCreditSale} 
          variant="contained" 
          disabled={loading || success || (customer.available_credit !== undefined && customer.available_credit < amount)}
          color="primary"
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : success ? '✓' : <CreditCardIcon />}
        >
          {loading ? 'Processing...' : success ? 'Success!' : 'Confirm Credit Sale'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ============ Cheque Payment Modal Component ============
const ChequePaymentModal = ({ 
  open, 
  onClose, 
  amount, 
  onProcessChequePayment,
  customer,
  invoiceNumber
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  
  const [chequeData, setChequeData] = useState({
    chequeNumber: '',
    bankName: '',
    branch: '',
    chequeDate: new Date().toISOString().split('T')[0],
    dueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    isPostDated: false,
    notes: `Payment for invoice ${invoiceNumber}`
  });

  useEffect(() => {
    if (open) {
      setChequeData({
        chequeNumber: '',
        bankName: '',
        branch: '',
        chequeDate: new Date().toISOString().split('T')[0],
        dueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        isPostDated: false,
        notes: `Payment for invoice ${invoiceNumber}`
      });
      setError(null);
      setSuccess(false);
    }
  }, [open, invoiceNumber]);

  const handleChequePayment = async () => {
    if (!chequeData.chequeNumber.trim()) {
      setError('Cheque number is required');
      return;
    }
    
    if (!chequeData.bankName.trim()) {
      setError('Bank name is required');
      return;
    }

    if (!chequeData.chequeDate) {
      setError('Cheque date is required');
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      await onProcessChequePayment(chequeData);
      setSuccess(true);
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err) {
      setError(err.message || 'Failed to process cheque payment');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <ChequeIcon color="primary" />
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Cheque Payment Details
          </Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Cheque payment details saved successfully!
          </Alert>
        )}
        
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            Customer: <strong>{customer?.name || 'Walk-in Customer'}</strong>
          </Typography>
          <Typography variant="body2" color="primary" gutterBottom>
            Invoice #: <strong>{invoiceNumber}</strong>
          </Typography>
          
          <Box sx={{ my: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
            <Typography variant="h6" align="center" sx={{ fontWeight: 'bold', color: '#2ecc71' }}>
              Amount: {formatCurrency(amount)}
            </Typography>
          </Box>
          
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Cheque Number *"
                value={chequeData.chequeNumber}
                onChange={(e) => setChequeData({...chequeData, chequeNumber: e.target.value})}
                disabled={loading || success}
                placeholder="Enter cheque number"
                required
                error={!chequeData.chequeNumber.trim() && error !== null}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Bank Name *"
                value={chequeData.bankName}
                onChange={(e) => setChequeData({...chequeData, bankName: e.target.value})}
                disabled={loading || success}
                placeholder="e.g., Commercial Bank"
                required
                error={!chequeData.bankName.trim() && error !== null}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Branch"
                value={chequeData.branch}
                onChange={(e) => setChequeData({...chequeData, branch: e.target.value})}
                disabled={loading || success}
                placeholder="e.g., Colombo"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Cheque Date *"
                type="date"
                value={chequeData.chequeDate}
                onChange={(e) => setChequeData({...chequeData, chequeDate: e.target.value})}
                InputLabelProps={{ shrink: true }}
                disabled={loading || success}
                required
                error={!chequeData.chequeDate && error !== null}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Due Date"
                type="date"
                value={chequeData.dueDate}
                onChange={(e) => setChequeData({...chequeData, dueDate: e.target.value})}
                InputLabelProps={{ shrink: true }}
                disabled={loading || success}
                helperText="When cheque can be cashed"
              />
            </Grid>
            
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={chequeData.isPostDated}
                    onChange={(e) => setChequeData({...chequeData, isPostDated: e.target.checked})}
                    disabled={loading || success}
                  />
                }
                label="Post-dated Cheque"
              />
              {chequeData.isPostDated && (
                <Typography variant="caption" color="warning.main" display="block">
                  Note: Post-dated cheque will be held until due date
                </Typography>
              )}
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes (Optional)"
                value={chequeData.notes}
                onChange={(e) => setChequeData({...chequeData, notes: e.target.value})}
                multiline
                rows={2}
                disabled={loading || success}
                placeholder="Additional notes about this cheque"
              />
            </Grid>
          </Grid>
          
          <Alert severity="info" sx={{ mt: 3 }}>
            <Typography variant="body2">
              <strong>Important:</strong> Verify cheque details before accepting. 
              The sale will be recorded as pending until cheque is cleared.
            </Typography>
          </Alert>
        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} color="inherit" disabled={loading}>
          Cancel
        </Button>
        <Button 
          onClick={handleChequePayment} 
          variant="contained" 
          disabled={loading || success || !chequeData.chequeNumber.trim() || !chequeData.bankName.trim() || !chequeData.chequeDate}
          color="primary"
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : success ? '✓' : <ChequeIcon />}
        >
          {loading ? 'Processing...' : success ? 'Success!' : 'Confirm Cheque Payment'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ============ Payment Status Modal Component ============
const PaymentStatusModal = ({ 
  open, 
  onClose, 
  onConfirm,
  transactionData
}) => {
  const [paymentStatus, setPaymentStatus] = useState('paid');
  const [loading, setLoading] = useState(false);

  const handleConfirm = () => {
    setLoading(true);
    setTimeout(() => {
      onConfirm(paymentStatus);
      setLoading(false);
      onClose();
    }, 500);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <ReceiptIcon color="primary" />
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Payment Status
          </Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              Please select the payment status for this receipt
            </Typography>
          </Alert>
          
          <FormControl component="fieldset" sx={{ width: '100%' }}>
            <FormLabel component="legend">Receipt Status</FormLabel>
            <RadioGroup
              value={paymentStatus}
              onChange={(e) => setPaymentStatus(e.target.value)}
            >
              <Paper 
                sx={{ 
                  p: 2, 
                  mb: 2, 
                  border: paymentStatus === 'paid' ? '2px solid #2e7d32' : '1px solid #e0e0e0',
                  bgcolor: paymentStatus === 'paid' ? '#e8f5e8' : 'transparent',
                  cursor: 'pointer'
                }}
                onClick={() => setPaymentStatus('paid')}
              >
                <FormControlLabel 
                  value="paid" 
                  control={<Radio />} 
                  label={
                    <Box display="flex" alignItems="center" gap={2}>
                      <PaidIcon sx={{ color: '#2e7d32' }} />
                      <Box>
                        <Typography variant="body1" fontWeight="bold" color="#2e7d32">
                          PAID
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Transaction completed, payment received
                        </Typography>
                      </Box>
                    </Box>
                  } 
                  sx={{ width: '100%', m: 0 }}
                />
              </Paper>
              
              <Paper 
                sx={{ 
                  p: 2, 
                  border: paymentStatus === 'pending' ? '2px solid #f57c00' : '1px solid #e0e0e0',
                  bgcolor: paymentStatus === 'pending' ? '#fff3e0' : 'transparent',
                  cursor: 'pointer'
                }}
                onClick={() => setPaymentStatus('pending')}
              >
                <FormControlLabel 
                  value="pending" 
                  control={<Radio />} 
                  label={
                    <Box display="flex" alignItems="center" gap={2}>
                      <PendingIcon sx={{ color: '#f57c00' }} />
                      <Box>
                        <Typography variant="body1" fontWeight="bold" color="#f57c00">
                          PENDING
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Awaiting payment or credit/cheque clearance
                        </Typography>
                      </Box>
                    </Box>
                  } 
                  sx={{ width: '100%', m: 0 }}
                />
              </Paper>
            </RadioGroup>
          </FormControl>
          
          <Box sx={{ mt: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              Invoice Summary:
            </Typography>
            <Typography variant="body2">
              Invoice #: <strong>{transactionData?.receipt_number || transactionData?.pos_number || 'N/A'}</strong>
            </Typography>
            <Typography variant="body2">
              Amount: <strong>{formatCurrency(transactionData?.total || 0)}</strong>
            </Typography>
            <Typography variant="body2">
              Customer: <strong>{transactionData?.customer || 'Walk-in Customer'}</strong>
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} color="inherit" disabled={loading}>
          Cancel
        </Button>
        <Button 
          onClick={handleConfirm} 
          variant="contained" 
          disabled={loading}
          color="primary"
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <ReceiptIcon />}
        >
          {loading ? 'Processing...' : 'Print Receipt'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ============ Quantity Dialog Component with Auto-focus and Keyboard Support ============
const QuantityDialog = ({ 
  open, 
  onClose, 
  product, 
  onConfirm,
  availableStock
}) => {
  const [quantity, setQuantity] = useState(1);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setQuantity(1);
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          inputRef.current.select();
        }
      }, 100);
    }
  }, [open]);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleConfirm();
    }
  };

  const handleConfirm = () => {
    if (quantity > 0 && quantity <= availableStock) {
      onConfirm(quantity);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <AddIcon color="primary" />
          <Typography variant="h6">
            Select Quantity
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        {product && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              <strong>{product.name}</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Code: {product.product_code} | Price: {formatCurrency(product.price)}
            </Typography>
            <Typography variant="body2" color={availableStock <= 10 ? 'warning.main' : 'success.main'} gutterBottom>
              Available Good Stock: {availableStock} units
            </Typography>
            
            <Box sx={{ mt: 3, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
              <IconButton 
                onClick={() => setQuantity(prev => Math.max(1, prev - 1))}
                sx={{ bgcolor: '#f0f0f0' }}
              >
                <RemoveIcon />
              </IconButton>
              <TextField
                inputRef={inputRef}
                type="number"
                value={quantity}
                onChange={(e) => {
                  let val = parseInt(e.target.value) || 1;
                  val = Math.min(availableStock, Math.max(1, val));
                  setQuantity(val);
                }}
                onKeyPress={handleKeyPress}
                InputProps={{
                  inputProps: { 
                    min: 1, 
                    max: availableStock, 
                    style: { textAlign: 'center', fontSize: '20px', fontWeight: 'bold' } 
                  }
                }}
                sx={{ width: 100, '& input': { textAlign: 'center' } }}
              />
              <IconButton 
                onClick={() => setQuantity(prev => Math.min(availableStock, prev + 1))}
                sx={{ bgcolor: '#f0f0f0' }}
              >
                <AddIcon />
              </IconButton>
            </Box>
            
            {product.track_serials && quantity > 1 && (
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <SerialIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                  You will need to select {quantity} serial numbers for this product.
                </Typography>
              </Alert>
            )}
            
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                💡 Tip: Type the quantity directly and press Enter
              </Typography>
            </Alert>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button 
          onClick={handleConfirm} 
          variant="contained" 
          color="primary"
          disabled={!product || quantity <= 0}
          startIcon={<AddIcon />}
        >
          Add to Cart ({quantity} {quantity === 1 ? 'item' : 'items'})
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ============ MAIN POS COMPONENT ============
const POS = () => {
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [cart, setCart] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState({ 
    id: 1, 
    name: 'Walk-in Customer',
    is_credit_customer: false,
    credit_limit: 0,
    available_credit: 0
  });
  const [discount, setDiscount] = useState({ type: 'percentage', value: 0 });
  const [payment, setPayment] = useState({
    method: 'cash',
    amountPaid: '',
    change: 0,
    chequeDetails: null
  });
  const [taxRate, setTaxRate] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [stats, setStats] = useState({});
  const [customerDialogOpen, setCustomerDialogOpen] = useState(false);
  const [editItemDialogOpen, setEditItemDialogOpen] = useState(false);
  const [currentEditItem, setCurrentEditItem] = useState(null);
  const [newCustomer, setNewCustomer] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    credit_limit: 0,
    customer_type: 'individual'
  });
  const [creditPaymentOpen, setCreditPaymentOpen] = useState(false);
  const [selectedCreditCustomer, setSelectedCreditCustomer] = useState(null);
  
  // Quantity Dialog State
  const [quantityDialogOpen, setQuantityDialogOpen] = useState(false);
  const [selectedProductForQuantity, setSelectedProductForQuantity] = useState(null);
  
  const [serialSelectionOpen, setSerialSelectionOpen] = useState(false);
  const [pendingProduct, setPendingProduct] = useState(null);
  const [pendingQuantity, setPendingQuantity] = useState(1);
  const [selectedSerials, setSelectedSerials] = useState([]);
  
  const [bulkSerialSelectionOpen, setBulkSerialSelectionOpen] = useState(false);
  const [pendingBulkProduct, setPendingBulkProduct] = useState(null);
  const [pendingBulkQuantity, setPendingBulkQuantity] = useState(1);
  const [pendingBulkSerials, setPendingBulkSerials] = useState([]);
  
  const [pendingBulkProducts, setPendingBulkProducts] = useState([]);
  const [pendingBulkPrice, setPendingBulkPrice] = useState('');
  const [pendingBulkProductIndex, setPendingBulkProductIndex] = useState(0);
  const [pendingBulkSelectedSerials, setPendingBulkSelectedSerials] = useState({});
  
  const [invoiceNumber, setInvoiceNumber] = useState('');
  
  const [bulkSaleDialogOpen, setBulkSaleDialogOpen] = useState(false);
  const [selectedBulkProducts, setSelectedBulkProducts] = useState([]);
  const [searchBulkTerm, setSearchBulkTerm] = useState('');
  const [bulkSalePrice, setBulkSalePrice] = useState('');
  
  const [chequePaymentOpen, setChequePaymentOpen] = useState(false);
  const [paymentStatusModalOpen, setPaymentStatusModalOpen] = useState(false);
  const [pendingReceiptData, setPendingReceiptData] = useState(null);
  
  const barcodeInputRef = useRef(null);
  const navigate = useNavigate();

  // Calculate totals
  const subtotal = cart.reduce((sum, item) => sum + parseFloat(item.line_total || 0), 0);
  const discountAmount = discount.type === 'percentage' 
    ? subtotal * (discount.value / 100)
    : discount.value;
  const taxAmount = (subtotal - discountAmount) * (taxRate / 100);
  const totalAmount = subtotal - discountAmount + taxAmount;

  useEffect(() => {
    fetchProducts();
    fetchCustomers();
    fetchStats();
    setInvoiceNumber(generateInvoiceNumber());
    if (barcodeInputRef.current) {
      barcodeInputRef.current.focus();
    }
  }, []);

  useEffect(() => {
    if (cart.length === 0 && !paymentDialogOpen && !creditPaymentOpen && !chequePaymentOpen && !paymentStatusModalOpen) {
      const newInvoiceNumber = generateInvoiceNumber();
      setInvoiceNumber(newInvoiceNumber);
    }
  }, [cart, paymentDialogOpen, creditPaymentOpen, chequePaymentOpen, paymentStatusModalOpen]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await getProducts();
      if (response.success) {
        console.log('📦 Products loaded:', response.data.length);
        setProducts(response.data);
      }
    } catch (err) {
      console.error('Error fetching products:', err);
      setError('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await getCustomers();
      if (response.success) {
        let allCustomers = response.data;
        
        allCustomers = allCustomers.map(customer => {
          const creditLimit = parseFloat(customer.credit_limit) || 0;
          const outstanding = parseFloat(customer.outstanding_balance) || 0;
          const available = parseFloat(customer.available_credit) || (creditLimit - outstanding);
          
          const isCreditCustomer = customer.is_credit_customer === true || creditLimit > 0;
          
          return {
            ...customer,
            is_credit_customer: isCreditCustomer,
            credit_limit: creditLimit,
            available_credit: available,
            outstanding_balance: outstanding,
            credit_utilization: creditLimit > 0 ? Math.round((outstanding / creditLimit) * 100) : 0
          };
        });
        
        setCustomers(allCustomers);
      }
    } catch (err) {
      console.error('Error fetching customers:', err);
      setCustomers([{
        id: 1,
        name: 'Walk-in Customer',
        phone: 'N/A',
        is_credit_customer: false,
        credit_limit: 0,
        available_credit: 0,
        outstanding_balance: 0
      }]);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await getPOSStats();
      if (response.success) {
        setStats(response.data);
      }
    } catch (err) {
      console.error('Error fetching POS stats:', err);
      setStats({
        today: { sales: '0.00', transactions: 0 },
        monthly: { sales: '0.00' }
      });
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleBarcodeKeyPress = (e) => {
    if (e.key === 'Enter' && searchTerm.trim()) {
      const product = products.find(p => 
        p.product_code === searchTerm.trim() || 
        (p.barcode && p.barcode === searchTerm.trim())
      );
      
      if (product) {
        if (product.is_active === false) {
          setError('Cannot add inactive product to cart');
          setTimeout(() => setError(''), 3000);
        } else {
          const availableGoodStock = Math.max(0, product.good_quantity || 0);
          if (availableGoodStock <= 0) {
            setError(`No good stock available for this product. Good stock: ${availableGoodStock}`);
            setTimeout(() => setError(''), 3000);
          } else {
            addToCart(product, 1);
            setSearchTerm('');
          }
        }
      } else {
        setError('Product not found!');
        setTimeout(() => setError(''), 3000);
      }
    }
  };

  // ============ UPDATED addToCart function with quantity dialog ============
  const addToCart = (product, quantity = 1) => {
    const availableGoodStock = Math.max(0, product.good_quantity || 0);
    
    console.log('🔍 Adding product:', {
      id: product.id,
      name: product.name,
      good_quantity: product.good_quantity,
      availableGoodStock: availableGoodStock,
      track_serials: product.track_serials
    });
    
    if (product.is_active === false) {
      setError('Cannot add inactive product to cart');
      setTimeout(() => setError(''), 3000);
      return;
    }

    if (availableGoodStock <= 0) {
      setError(`No good stock available for this product. Good stock: ${availableGoodStock}`);
      setTimeout(() => setError(''), 3000);
      return;
    }

    // If quantity is provided (from bulk add or barcode), use it directly
    if (quantity > 1) {
      if (availableGoodStock < quantity) {
        setError(`Only ${availableGoodStock} good units available. You requested ${quantity}.`);
        setTimeout(() => setError(''), 3000);
        return;
      }
      
      if (product.track_serials) {
        setPendingProduct(product);
        setPendingQuantity(quantity);
        setSerialSelectionOpen(true);
      } else {
        const newItem = createCartItem(product, quantity, []);
        setCurrentEditItem(newItem);
        setEditItemDialogOpen(true);
      }
    } else {
      // For single quantity, open quantity selection dialog first
      setSelectedProductForQuantity(product);
      setQuantityDialogOpen(true);
    }
  };

  // ============ Handle quantity confirmation ============
  const handleQuantityConfirm = (quantity) => {
    const product = selectedProductForQuantity;
    const availableGoodStock = Math.max(0, product.good_quantity || 0);
    
    if (quantity > availableGoodStock) {
      setError(`Only ${availableGoodStock} good units available. You requested ${quantity}.`);
      setTimeout(() => setError(''), 3000);
      setQuantityDialogOpen(false);
      return;
    }
    
    if (product.track_serials) {
      setPendingProduct(product);
      setPendingQuantity(quantity);
      setSerialSelectionOpen(true);
    } else {
      const newItem = createCartItem(product, quantity, []);
      setCurrentEditItem(newItem);
      setEditItemDialogOpen(true);
    }
    
    setQuantityDialogOpen(false);
    setSelectedProductForQuantity(null);
  };

  const createCartItem = (product, quantity, serialNumbers) => {
    return {
      id: Date.now(),
      product_id: product.id,
      name: product.name,
      product_name: product.name,
      product_code: product.product_code,
      quantity: quantity,
      unit_price: product.price,
      discount_percentage: 0,
      tax_rate: taxRate,
      line_total: product.price * quantity,
      stock: product.quantity,
      good_stock: Math.max(0, product.good_quantity || 0),
      warranty_months: product.warranty_months || 0,
      warranty_terms: product.warranty_terms || '',
      serial_numbers: serialNumbers,
      track_serials: product.track_serials || false
    };
  };

  const handleSerialSelection = (selectedSerials) => {
    console.log('✅ Serial selection confirmed:', selectedSerials);
    setSelectedSerials(selectedSerials);
    
    if (pendingProduct) {
      const serialNumbers = selectedSerials.map(s => s.serial_number);
      const newItem = createCartItem(pendingProduct, pendingQuantity, serialNumbers);
      setCurrentEditItem(newItem);
      setEditItemDialogOpen(true);
    }
  };

  // ============ FIXED: Handle bulk serial selection with multiple products ============
  const handleBulkSerialSelection = (selectedSerials) => {
    console.log('✅ Bulk serial selection confirmed:', selectedSerials);
    console.log('Selected serials:', selectedSerials);
    
    // Update the current product with selected serials
    const updatedProducts = [...pendingBulkProducts];
    const currentProduct = updatedProducts[pendingBulkProductIndex];
    
    if (currentProduct) {
      // Store selected serials for this product
      const serialNumbers = selectedSerials.map(s => s.serial_number);
      const serialObjects = selectedSerials.map(s => ({
        id: s.id,
        serial_number: s.serial_number,
        product_id: currentProduct.id
      }));
      
      currentProduct.selected_serials = serialNumbers;
      updatedProducts[pendingBulkProductIndex] = currentProduct;
      setPendingBulkProducts(updatedProducts);
      
      // Add to pending serials
      const newPendingSerials = [...pendingBulkSerials, ...serialObjects];
      setPendingBulkSerials(newPendingSerials);
      
      console.log('Updated pendingBulkSerials:', newPendingSerials);
    }
    
    // Find next product that needs serials
    const nextIndex = pendingBulkProductIndex + 1;
    const nextProductNeedingSerials = updatedProducts.slice(nextIndex).find(p => p.track_serials);
    
    if (nextProductNeedingSerials) {
      // Process next product
      setPendingBulkProduct(nextProductNeedingSerials);
      setPendingBulkQuantity(nextProductNeedingSerials.quantity);
      setPendingBulkProductIndex(nextIndex);
      setBulkSerialSelectionOpen(true);
    } else {
      // All products processed, add to cart
      console.log('All serials selected, processing bulk sale with products:', updatedProducts);
      console.log('All serials:', pendingBulkSerials);
      processBulkSale(updatedProducts, pendingBulkPrice);
      setBulkSerialSelectionOpen(false);
      setBulkSaleDialogOpen(false);
      // Reset bulk states
      setPendingBulkProducts([]);
      setPendingBulkPrice('');
      setPendingBulkSerials([]);
      setPendingBulkProductIndex(0);
      setPendingBulkSelectedSerials({});
    }
  };

  const updateCartItem = (updatedItem) => {
    setCart(prevCart => {
      const existingIndex = prevCart.findIndex(item => item.id === updatedItem.id);
      
      if (existingIndex >= 0) {
        const newCart = [...prevCart];
        newCart[existingIndex] = updatedItem;
        return newCart;
      } else {
        return [...prevCart, updatedItem];
      }
    });
    setEditItemDialogOpen(false);
    setCurrentEditItem(null);
    setSelectedSerials([]);
  };

  const updateQuantity = (id, delta) => {
    setCart(prevCart => {
      return prevCart.map(item => {
        if (item.id === id) {
          const newQuantity = item.quantity + delta;
          
          if (newQuantity < 1) {
            return item;
          }
          
          if (newQuantity > item.good_stock) {
            setError(`Only ${item.good_stock} good items available in stock`);
            setTimeout(() => setError(''), 3000);
            return item;
          }
          
          if (item.track_serials && delta > 0) {
            setPendingProduct(products.find(p => p.id === item.product_id));
            setPendingQuantity(newQuantity - item.quantity);
            setSerialSelectionOpen(true);
            return item;
          }
          
          const newLineTotal = (item.unit_price * newQuantity) * (1 - (item.discount_percentage / 100));
          
          return { 
            ...item, 
            quantity: newQuantity,
            line_total: newLineTotal
          };
        }
        return item;
      });
    });
  };

  const editItem = (item) => {
    setCurrentEditItem(item);
    setEditItemDialogOpen(true);
  };

  const removeFromCart = (id) => {
    setCart(prevCart => prevCart.filter(item => item.id !== id));
  };

  const handlePaymentChange = (field, value) => {
    if (field === 'method' && value === 'credit') {
      if (selectedCustomer.id === 1) {
        setError('Please select a customer for credit payment');
        setPayment(prev => ({ ...prev, method: 'cash' }));
        return;
      }
      
      const customerData = customers.find(c => c.id === selectedCustomer.id);
      if (!customerData?.is_credit_customer) {
        setError('Selected customer is not approved for credit');
        setPayment(prev => ({ ...prev, method: 'cash' }));
        return;
      }
      
      if (customerData.available_credit < totalAmount) {
        setError(`Customer credit limit exceeded. Available: ${formatCurrency(customerData.available_credit)}`);
        setPayment(prev => ({ ...prev, method: 'cash' }));
        return;
      }
      
      setSelectedCreditCustomer(customerData);
      setCreditPaymentOpen(true);
      return;
    }
    
    if (field === 'method' && value === 'cheque') {
      setPayment(prev => ({
        ...prev,
        method: value,
        amountPaid: totalAmount,
        change: 0,
        chequeDetails: null
      }));
      setChequePaymentOpen(true);
      return;
    }
    
    setPayment(prev => ({
      ...prev,
      [field]: value,
      change: field === 'amountPaid' 
        ? parseFloat(value || 0) - totalAmount 
        : prev.change
    }));
  };

  const handleCreateCustomer = async () => {
    if (!newCustomer.name.trim()) {
      setError('Customer name is required');
      return;
    }

    try {
      const customerData = {
        ...newCustomer,
        credit_limit: parseFloat(newCustomer.credit_limit) || 0,
        customer_code: `CUST-${Date.now().toString().slice(-6)}`
      };

      const response = await createCustomer(customerData);
      if (response.success) {
        await fetchCustomers();
        setSelectedCustomer({
          id: response.data.id,
          name: newCustomer.name,
          is_credit_customer: parseFloat(newCustomer.credit_limit) > 0,
          credit_limit: parseFloat(newCustomer.credit_limit) || 0,
          available_credit: parseFloat(newCustomer.credit_limit) || 0
        });
        setNewCustomer({ 
          name: '', 
          phone: '', 
          email: '', 
          address: '',
          credit_limit: 0,
          customer_type: 'individual'
        });
        setCustomerDialogOpen(false);
        setSuccess('Customer added successfully');
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      setError(err.message || 'Failed to create customer');
    }
  };

  const createCreditSale = async (customer, paymentData, amount, invoiceNumber) => {
    try {
      const posData = {
        customer_id: customer.id,
        customer_name: customer.name,
        items: cart.map(item => ({
          product_id: item.product_id,
          product_name: item.product_name || item.name,
          quantity: item.quantity,
          unit_price: item.unit_price,
          discount_percentage: item.discount_percentage,
          tax_rate: item.tax_rate,
          warranty_months: item.warranty_months,
          warranty_terms: item.warranty_terms,
          serial_numbers: item.serial_numbers || [],
          is_bulk_sale: item.is_bulk_sale || false,
          original_price: item.original_price || null,
          bulk_discount_percentage: item.bulk_discount_percentage || null
        })),
        discount_type: discount.type,
        discount_value: discount.value,
        payment_method: 'credit',
        amount_paid: 0,
        change_amount: 0,
        tax_rate: taxRate,
        total_amount: totalAmount,
        notes: `Credit Sale - Due Date: ${paymentData.dueDate} | ${paymentData.description || 'No description'}`,
        is_credit_sale: true,
        credit_amount: amount,
        credit_due_date: paymentData.dueDate,
        pos_number: invoiceNumber
      };

      const response = await createPOS(posData);
      return response;
    } catch (err) {
      throw err;
    }
  };

  const handleProcessChequePayment = async (chequeData) => {
    console.log('💰 Processing cheque payment with data:', chequeData);
    
    if (!chequeData || !chequeData.chequeNumber || !chequeData.bankName || !chequeData.chequeDate) {
      setError('Cheque number, bank name, and cheque date are required');
      return;
    }
    
    await processPaymentWithCheque(chequeData);
  };

  const processPaymentWithCheque = async (chequeData) => {
    if (cart.length === 0) {
      setError('Cart is empty');
      return;
    }

    setLoading(true);
    
    const currentInvoiceNumber = invoiceNumber;
    
    const posData = {
      customer_id: selectedCustomer.id,
      customer_name: selectedCustomer.name,
      items: cart.map(item => ({
        product_id: item.product_id,
        product_name: item.product_name || item.name,
        quantity: parseInt(item.quantity) || 1,
        unit_price: parseFloat(item.unit_price) || 0,
        discount_percentage: parseFloat(item.discount_percentage) || 0,
        tax_rate: parseFloat(item.tax_rate) || 0,
        warranty_months: parseInt(item.warranty_months) || 0,
        warranty_terms: item.warranty_terms || '',
        serial_numbers: item.serial_numbers || [],
        is_bulk_sale: item.is_bulk_sale || false,
        original_price: item.original_price ? parseFloat(item.original_price) : null,
        bulk_discount_percentage: item.bulk_discount_percentage ? parseFloat(item.bulk_discount_percentage) : null
      })),
      discount_type: discount.type,
      discount_value: parseFloat(discount.value) || 0,
      payment_method: 'cheque',
      amount_paid: parseFloat(totalAmount) || 0,
      change_amount: 0,
      tax_rate: parseFloat(taxRate) || 0,
      notes: `Cheque Payment - ${new Date().toLocaleString()}`,
      pos_number: currentInvoiceNumber,
      
      cheque_number: chequeData.chequeNumber || '',
      cheque_bank: chequeData.bankName || '',
      cheque_branch: chequeData.branch || '',
      cheque_date: chequeData.chequeDate || new Date().toISOString().split('T')[0],
      cheque_due_date: chequeData.dueDate || chequeData.chequeDate || new Date().toISOString().split('T')[0],
      is_post_dated_cheque: chequeData.isPostDated || false,
      cheque_status: 'pending',
      cheque_details: {
        chequeNumber: chequeData.chequeNumber || '',
        bankName: chequeData.bankName || '',
        branch: chequeData.branch || '',
        chequeDate: chequeData.chequeDate || '',
        dueDate: chequeData.dueDate || chequeData.chequeDate || '',
        isPostDated: chequeData.isPostDated || false,
        notes: chequeData.notes || `Payment for invoice ${currentInvoiceNumber}`
      }
    };

    console.log('📤 Sending CHEQUE payment data:', JSON.stringify(posData, null, 2));

    try {
      const response = await createPOS(posData);
      
      if (response.success) {
        setSuccess('Cheque payment recorded successfully!');
        
        setChequePaymentOpen(false);
        
        const receiptData = {
          ...response.data,
          payment_method: 'cheque',
          customer: selectedCustomer.name,
          items: cart,
          subtotal: subtotal,
          discount: discountAmount,
          tax: taxAmount,
          total: totalAmount,
          paid: totalAmount,
          change: 0,
          date: new Date().toISOString(),
          receipt_number: currentInvoiceNumber,
          pos_number: currentInvoiceNumber,
          cheque_details: chequeData,
          is_cheque_payment: true,
          status: 'pending'
        };
        
        setPendingReceiptData(receiptData);
        setPaymentStatusModalOpen(true);
        
        setCart([]);
        setPayment({
          method: 'cash',
          amountPaid: '',
          change: 0,
          chequeDetails: null
        });
        
        setPaymentDialogOpen(false);
        
        const newInvoiceNumber = generateInvoiceNumber();
        setInvoiceNumber(newInvoiceNumber);
        
        await refreshAfterSale();
        
        setTimeout(() => setSuccess(''), 5000);
      }
    } catch (err) {
      console.error('❌ Cheque transaction failed:', err);
      setError(err.message || 'Cheque transaction failed');
    } finally {
      setLoading(false);
    }
  };

  const processPayment = async () => {
    if (cart.length === 0) {
      setError('Cart is empty');
      return;
    }

    if (payment.method === 'cheque') {
      return;
    }

    if (payment.method !== 'credit' && parseFloat(payment.amountPaid || 0) < totalAmount) {
      setError('Insufficient payment amount');
      return;
    }

    setLoading(true);
    
    const currentInvoiceNumber = invoiceNumber;
    
    const posData = {
      customer_id: selectedCustomer.id,
      customer_name: selectedCustomer.name,
      items: cart.map(item => ({
        product_id: item.product_id,
        product_name: item.product_name || item.name,
        quantity: parseInt(item.quantity) || 1,
        unit_price: parseFloat(item.unit_price) || 0,
        discount_percentage: parseFloat(item.discount_percentage) || 0,
        tax_rate: parseFloat(item.tax_rate) || 0,
        warranty_months: parseInt(item.warranty_months) || 0,
        warranty_terms: item.warranty_terms || '',
        serial_numbers: item.serial_numbers || [],
        is_bulk_sale: item.is_bulk_sale || false,
        original_price: item.original_price ? parseFloat(item.original_price) : null,
        bulk_discount_percentage: item.bulk_discount_percentage ? parseFloat(item.bulk_discount_percentage) : null
      })),
      discount_type: discount.type,
      discount_value: parseFloat(discount.value) || 0,
      payment_method: payment.method,
      amount_paid: parseFloat(payment.amountPaid || 0),
      change_amount: parseFloat(payment.change || 0),
      tax_rate: parseFloat(taxRate) || 0,
      notes: `POS Transaction - ${new Date().toLocaleString()}`,
      pos_number: currentInvoiceNumber
    };

    console.log('📤 Sending payment data:', JSON.stringify(posData, null, 2));

    try {
      const response = await createPOS(posData);
      
      if (response.success) {
        setSuccess('Transaction completed successfully!');
        
        const receiptData = {
          ...response.data,
          payment_method: payment.method,
          customer: selectedCustomer.name,
          items: cart,
          subtotal: subtotal,
          discount: discountAmount,
          tax: taxAmount,
          total: totalAmount,
          paid: payment.amountPaid,
          change: payment.change,
          date: new Date().toISOString(),
          receipt_number: currentInvoiceNumber,
          pos_number: currentInvoiceNumber,
          status: 'paid'
        };
        
        setPendingReceiptData(receiptData);
        setPaymentStatusModalOpen(true);
        
        setCart([]);
        setPayment({
          method: 'cash',
          amountPaid: '',
          change: 0,
          chequeDetails: null
        });
        
        setPaymentDialogOpen(false);
        
        const newInvoiceNumber = generateInvoiceNumber();
        setInvoiceNumber(newInvoiceNumber);
        
        await refreshAfterSale();
        
        setTimeout(() => setSuccess(''), 5000);
      }
    } catch (err) {
      console.error('❌ Transaction failed:', err);
      setError(err.message || 'Transaction failed');
    } finally {
      setLoading(false);
    }
  };

  const refreshAfterSale = async () => {
    console.log('🔄 Auto-refreshing data after sale...');
    try {
      await Promise.all([
        fetchProducts(),
        fetchCustomers(),
        fetchStats()
      ]);
      console.log('✅ Data refreshed successfully');
    } catch (err) {
      console.error('❌ Error refreshing data:', err);
    }
  };

  const handleCreditPaymentSuccess = (transactionData, invoiceNumber, dueDate) => {
    setSuccess('Credit transaction completed successfully!');
    
    if (selectedCreditCustomer) {
      const newOutstanding = (selectedCreditCustomer.outstanding_balance || 0) + totalAmount;
      const newAvailable = (selectedCreditCustomer.available_credit || 0) - totalAmount;
      
      setCustomers(prev => 
        prev.map(c => 
          c.id === selectedCreditCustomer.id ? {
            ...c,
            outstanding_balance: newOutstanding,
            available_credit: newAvailable
          } : c
        )
      );
      
      if (selectedCustomer.id === selectedCreditCustomer.id) {
        setSelectedCustomer(prev => ({
          ...prev,
          outstanding_balance: newOutstanding,
          available_credit: newAvailable
        }));
      }
    }
    
    const receiptData = {
      ...transactionData,
      payment_method: 'credit',
      customer: selectedCustomer.name,
      items: cart,
      subtotal: subtotal,
      discount: discountAmount,
      tax: taxAmount,
      total: totalAmount,
      paid: 0,
      change: 0,
      date: new Date().toISOString(),
      receipt_number: invoiceNumber,
      pos_number: invoiceNumber,
      credit_reference: transactionData.transaction?.reference_number || 'N/A',
      due_date: dueDate ? new Date(dueDate).toLocaleDateString() : 'N/A'
    };
    
    setPendingReceiptData(receiptData);
    setPaymentStatusModalOpen(true);
    
    setCart([]);
    setPayment({
      method: 'cash',
      amountPaid: '',
      change: 0,
      chequeDetails: null
    });
    
    setPaymentDialogOpen(false);
    
    const newInvoiceNumber = generateInvoiceNumber();
    setInvoiceNumber(newInvoiceNumber);
    
    fetchStats();
    fetchCustomers();
    
    setTimeout(() => setSuccess(''), 5000);
  };

  const handlePaymentStatusConfirm = (status) => {
    if (pendingReceiptData) {
      const updatedReceiptData = {
        ...pendingReceiptData,
        status: status
      };
      
      printReceipt(updatedReceiptData);
      setPendingReceiptData(null);
    }
  };

  // Bulk Sale Functions - FIXED with serial handling
  const handleAddToBulkSale = (product) => {
    if (product.is_active === false) {
      setError('Cannot add inactive product to bulk sale');
      setTimeout(() => setError(''), 3000);
      return;
    }

    const availableGoodStock = Math.max(0, product.good_quantity || 0);
    if (availableGoodStock <= 0) {
      setError('No good stock available for this product');
      setTimeout(() => setError(''), 3000);
      return;
    }

    setSelectedBulkProducts(prev => {
      const existing = prev.find(p => p.id === product.id);
      if (existing) {
        if (existing.quantity + 1 > availableGoodStock) {
          setError(`Only ${availableGoodStock} good items available in stock for ${product.name}`);
          setTimeout(() => setError(''), 3000);
          return prev;
        }
        return prev.map(p =>
          p.id === product.id
            ? { ...p, quantity: p.quantity + 1, line_total: p.price * (p.quantity + 1), selected_serials: [] }
            : p
        );
      } else {
        return [...prev, {
          ...product,
          quantity: 1,
          line_total: product.price,
          good_stock: availableGoodStock,
          selected_serials: []
        }];
      }
    });
  };

  const updateBulkQuantity = (id, delta) => {
    setSelectedBulkProducts(prev =>
      prev.map(item => {
        if (item.id === id) {
          const newQuantity = item.quantity + delta;
          if (newQuantity < 1) return null;
          
          const product = products.find(p => p.id === item.id);
          const availableGoodStock = Math.max(0, product?.good_quantity || 0);
          
          if (newQuantity > availableGoodStock) {
            setError(`Only ${availableGoodStock} good items available in stock for ${item.name}`);
            setTimeout(() => setError(''), 3000);
            return item;
          }
          
          // Clear selected serials if quantity changed
          const selectedSerials = (delta !== 0 && item.track_serials) ? [] : item.selected_serials;
          
          return {
            ...item,
            quantity: newQuantity,
            line_total: item.price * newQuantity,
            selected_serials: selectedSerials
          };
        }
        return item;
      }).filter(item => item !== null)
    );
  };

  const removeFromBulkSale = (id) => {
    setSelectedBulkProducts(prev => prev.filter(item => item.id !== id));
  };

  const calculateBulkTotal = () => {
    return selectedBulkProducts.reduce((sum, item) => sum + item.line_total, 0);
  };

  // ============ FIXED: Handle confirm bulk sale with serial selection ============
  const handleConfirmBulkSale = () => {
    if (selectedBulkProducts.length === 0) {
      setError('Please add products to bulk sale');
      return;
    }

    if (!bulkSalePrice || parseFloat(bulkSalePrice) <= 0) {
      setError('Please enter a valid bulk sale price');
      return;
    }

    // Store the bulk products and price
    setPendingBulkProducts(selectedBulkProducts);
    setPendingBulkPrice(bulkSalePrice);
    setPendingBulkSerials([]);
    setPendingBulkProductIndex(0);

    // Find first product that needs serials and hasn't been selected yet
    const productsNeedingSerials = selectedBulkProducts.filter(p => 
      p.track_serials && (!p.selected_serials || p.selected_serials.length < p.quantity)
    );
    
    if (productsNeedingSerials.length > 0) {
      // Close the bulk dialog and start serial selection
      setBulkSaleDialogOpen(false);
      const firstProduct = productsNeedingSerials[0];
      setPendingBulkProduct(firstProduct);
      setPendingBulkQuantity(firstProduct.quantity);
      setBulkSerialSelectionOpen(true);
    } else {
      // No serials needed, process directly
      processBulkSale(selectedBulkProducts, bulkSalePrice);
      setBulkSaleDialogOpen(false);
    }
  };

  // ============ FIXED: Process bulk sale with serial numbers ============
  const processBulkSale = (products, price) => {
    const bulkPrice = parseFloat(price);
    const totalOriginalPrice = products.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    const ratio = bulkPrice / totalOriginalPrice;

    console.log('Processing bulk sale with products:', products);
    console.log('Pending bulk serials:', pendingBulkSerials);

    products.forEach(product => {
      const adjustedPrice = product.price * ratio;
      const discountPercentage = 100 - (ratio * 100);
      
      // Get serials for this product from pendingBulkSerials
      const productSerials = pendingBulkSerials.filter(s => s.product_id === product.id);
      const serialNumbers = productSerials.map(s => s.serial_number);
      
      console.log(`Product ${product.name} serials:`, serialNumbers);
      
      const newItem = {
        id: Date.now() + product.id,
        product_id: product.id,
        name: product.name,
        product_name: product.name,
        product_code: product.product_code,
        quantity: product.quantity,
        unit_price: adjustedPrice,
        discount_percentage: Math.max(0, discountPercentage.toFixed(2)),
        tax_rate: taxRate,
        line_total: adjustedPrice * product.quantity,
        stock: product.quantity,
        good_stock: Math.max(0, product.good_quantity || 0),
        warranty_months: product.warranty_months || 0,
        warranty_terms: product.warranty_terms || '',
        serial_numbers: serialNumbers,
        is_bulk_sale: true,
        original_price: product.price,
        bulk_discount_percentage: discountPercentage > 0 ? discountPercentage.toFixed(2) : 0,
        track_serials: product.track_serials || false
      };
      setCart(prevCart => [...prevCart, newItem]);
    });

    const totalItems = products.reduce((sum, item) => sum + item.quantity, 0);
    setSuccess(`Added ${totalItems} items (${products.length} products) as bulk sale`);
    setTimeout(() => setSuccess(''), 3000);
    
    // Clear bulk sale state
    setSelectedBulkProducts([]);
    setBulkSalePrice('');
    setPendingBulkProducts([]);
    setPendingBulkPrice('');
    setPendingBulkSerials([]);
    setPendingBulkProductIndex(0);
  };

  // Print Receipt Function (kept as is from previous version)
  const printReceipt = (receiptData) => {
    const receiptWindow = window.open('', '_blank');
    
    const itemsWithWarranty = receiptData.items?.filter(item => item.warranty_months > 0) || [];
    const itemsWithSerials = receiptData.items?.filter(item => 
      item.serial_numbers && item.serial_numbers.length > 0
    ) || [];
    
    const hasBulkSaleItems = receiptData.items?.some(item => item.is_bulk_sale) || false;
    const isCreditTransaction = receiptData.payment_method === 'credit';
    const isChequeTransaction = receiptData.payment_method === 'cheque';
    
    const paymentStatus = receiptData.status || 
                         (isChequeTransaction ? 'pending' : 
                          isCreditTransaction ? 'pending' : 'paid');
    
    const warrantyItemsWithDates = itemsWithWarranty.map(item => {
      const purchaseDate = new Date(receiptData.date);
      const warrantyEndDate = new Date(purchaseDate);
      warrantyEndDate.setMonth(warrantyEndDate.getMonth() + (item.warranty_months || 0));
      return {
        ...item,
        warranty_end_date: warrantyEndDate.toLocaleDateString()
      };
    });
    
    const bulkSaleItems = receiptData.items?.filter(item => item.is_bulk_sale) || [];
    const regularItems = receiptData.items?.filter(item => !item.is_bulk_sale) || [];
    
    const formatPaymentMethod = (method) => {
      switch(method) {
        case 'cash': return 'Cash';
        case 'card': return 'Card';
        case 'bank_transfer': return 'Bank Transfer';
        case 'online': return 'Online Payment';
        case 'credit': return 'Credit';
        case 'cheque': return 'Cheque';
        default: return method?.toUpperCase() || 'CASH';
      }
    };
    
    const safeNumber = (value) => {
      if (value === null || value === undefined) return 0;
      const num = parseFloat(value);
      return isNaN(num) ? 0 : num;
    };
    
    const formatNumber = (value) => {
      const num = safeNumber(value);
      return num.toFixed(2);
    };
    
    const displayNumber = receiptData.receipt_number || receiptData.pos_number || '';
    
    receiptWindow.document.write(`
<!DOCTYPE html>
<html>
<head>
  <title>Invoice #${displayNumber}</title>
  <style>
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
    
    @media print {
      @page { 
        size: 80mm auto; 
        margin: 0; 
      }
      body { 
        margin: 0; 
        padding: 0;
      }
      .no-print { 
        display: none !important; 
      }
    }
    
    body { 
      font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif; 
      font-size: 11px;
      padding: 0;
      margin: 0;
      max-width: 74mm;
      margin: 0 auto;
      background: white;
      color: #1e293b;
      line-height: 1.25;
      font-weight: 500;
    }
    
    .receipt-container {
      padding: 1.5mm 2mm 2mm 2mm;
    }
    
    .header {
      text-align: center;
      margin-bottom: 1.8mm;
      padding-bottom: 1.2mm;
      border-bottom: 2px solid #2563eb;
    }
    
    .company-name {
      font-size: 18px;
      font-weight: 800;
      color: #0f172a;
      letter-spacing: -0.2px;
    }
    
    .store-info {
      font-size: 7.5px;
      color: #334155;
      line-height: 1.2;
      margin-top: 1px;
      font-weight: 500;
    }
    
    .invoice-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 1.8mm;
      padding: 1.2mm 1.5mm;
      background: #f1f5f9;
      border-radius: 5px;
    }
    
    .invoice-title {
      font-size: 12px;
      font-weight: 800;
      color: #2563eb;
    }
    
    .invoice-number {
      font-family: monospace;
      font-weight: 700;
      font-size: 10px;
      color: #0f172a;
    }
    
    .status-badge {
      display: inline-flex;
      align-items: center;
      gap: 2px;
      padding: 1px 5px;
      border-radius: 16px;
      font-size: 7px;
      font-weight: 700;
      margin-left: 3px;
    }
    
    .status-paid {
      background: #dcfce7;
      color: #166534;
    }
    
    .status-pending {
      background: #fed7aa;
      color: #9a3412;
    }
    
    .payment-badge {
      display: inline-block;
      padding: 1px 5px;
      border-radius: 4px;
      font-size: 6.5px;
      font-weight: 700;
      margin-left: 3px;
      text-transform: uppercase;
    }
    
    .badge-credit {
      background: #fef3c7;
      color: #92400e;
    }
    
    .badge-cheque {
      background: #dbeafe;
      color: #1e40af;
    }
    
    .customer-info {
      margin-bottom: 1.8mm;
      padding: 1.2mm 1.5mm;
      background: #ffffff;
      border: 1px solid #e2e8f0;
      border-radius: 5px;
    }
    
    .customer-row-duo {
      display: flex;
      justify-content: space-between;
      align-items: baseline;
      margin-bottom: 1px;
      line-height: 1.25;
    }
    
    .customer-label {
      color: #64748b;
      font-weight: 600;
      font-size: 7.5px;
      letter-spacing: 0.2px;
    }
    
    .customer-value {
      font-weight: 700;
      color: #0f172a;
      font-size: 8.5px;
    }
    
    .info-group {
      display: flex;
      align-items: baseline;
      gap: 5px;
    }
    
    .payment-details {
      margin-bottom: 1.8mm;
      padding: 1.2mm;
      border-radius: 5px;
      font-size: 7.5px;
    }
    
    .cheque-details {
      background: #dbeafe;
      border-left: 3px solid #2563eb;
    }
    
    .credit-details {
      background: #fef3c7;
      border-left: 3px solid #d97706;
    }
    
    .payment-title {
      font-weight: 800;
      font-size: 9px;
      margin-bottom: 1px;
    }
    
    .payment-row {
      display: flex;
      justify-content: space-between;
      font-size: 7.5px;
      margin-top: 1px;
      font-weight: 500;
    }
    
    /* ITEMS TABLE - with large item name and separate row for QTY/PRICE/TOTAL */
    .items-table {
      width: 100%;
      border-collapse: collapse;
      margin: 1.8mm 0;
    }
    
    .items-table th {
      background: #1e293b;
      color: white;
      padding: 1mm 0.8mm;
      font-weight: 700;
      font-size: 7px;
      text-transform: uppercase;
      letter-spacing: 0.3px;
    }
    
    .items-table td {
      padding: 1.5mm 0.8mm;
      border-bottom: 1px solid #eef2f6;
      vertical-align: top;
    }
    
    .item-name-large {
      font-size: 11px;
      font-weight: 800;
      color: #0a0c10;
      margin-bottom: 3px;
      line-height: 1.2;
      display: block;
    }
    
    .item-meta-row {
      display: flex;
      justify-content: space-between;
      align-items: baseline;
      flex-wrap: wrap;
      gap: 6px;
      font-size: 8px;
      font-weight: 600;
      background: #f9fafc;
      padding: 2px 4px;
      border-radius: 4px;
      margin-top: 2px;
    }
    
    .meta-qty {
      color: #2563eb;
      background: #eef2ff;
      padding: 1px 5px;
      border-radius: 12px;
      font-weight: 800;
    }
    
    .meta-unit-price {
      color: #475569;
      font-weight: 700;
    }
    
    .meta-total {
      color: #0f172a;
      font-weight: 800;
      background: #e6f0ff;
      padding: 1px 5px;
      border-radius: 12px;
    }
    
    .item-extras {
      margin-top: 2px;
      font-size: 6.5px;
      display: flex;
      flex-wrap: wrap;
      gap: 4px;
      align-items: center;
    }
    
    .warranty-info {
      background: #ecfdf5;
      color: #047857;
      padding: 1px 4px;
      border-radius: 3px;
      font-weight: 700;
    }
    
    .serial-info {
      background: #eef2ff;
      color: #1e40af;
      font-family: monospace;
      padding: 1px 4px;
      border-radius: 3px;
    }
    
    .discount-info {
      background: #fef9c3;
      color: #854d0e;
      padding: 1px 4px;
      border-radius: 3px;
    }
    
    .bundle-section {
      background: #fef3c7;
      padding: 1.2mm;
      border-radius: 5px;
      margin: 1.8mm 0;
    }
    
    .bundle-title {
      font-weight: 800;
      color: #92400e;
      font-size: 9px;
      margin-bottom: 1mm;
    }
    
    .bundle-item {
      display: flex;
      justify-content: space-between;
      font-size: 7.5px;
      padding: 0.6mm 0;
      border-bottom: 1px dashed #fbbf24;
      font-weight: 500;
    }
    
    .bundle-total {
      display: flex;
      justify-content: space-between;
      font-weight: 800;
      margin-top: 1mm;
      padding-top: 0.8mm;
      border-top: 2px solid #fbbf24;
      color: #92400e;
      font-size: 8px;
    }
    
    .totals {
      margin: 1.8mm 0;
      padding: 1.2mm;
      background: #f8fafc;
      border-radius: 5px;
    }
    
    .total-row {
      display: flex;
      justify-content: space-between;
      padding: 0.8mm 0;
      font-size: 8.5px;
      font-weight: 600;
      line-height: 1.2;
    }
    
    .grand-total {
      display: flex;
      justify-content: space-between;
      font-weight: 800;
      font-size: 11px;
      color: #2563eb;
      padding-top: 1.2mm;
      margin-top: 0.5mm;
      border-top: 2px solid #2563eb;
    }
    
    .compact-payment-row {
      padding: 0.4mm 0;
    }
    
    .footer {
      text-align: center;
      margin-top: 2mm;
      padding-top: 1.5mm;
      border-top: 1px dashed #cbd5e1;
    }
    
    .footer-main {
      font-weight: 800;
      color: #2563eb;
      font-size: 8px;
      margin-bottom: 0.5mm;
      line-height: 1.2;
    }
    
    .footer-services {
      font-size: 6.5px;
      font-weight: 500;
      line-height: 1.2;
      margin: 0.8mm 0;
    }
    
    /* Increased text size for footer dev */
    .footer-dev {
      font-size: 7px;
      color: #64748b;
      margin-top: 1.5mm;
      font-weight: 500;
      line-height: 1.3;
      padding-top: 1mm;
      border-top: 1px dashed #cbd5e1;
    }
    
    .print-button {
      position: fixed;
      bottom: 12px;
      right: 12px;
      background: #2563eb;
      color: white;
      border: none;
      padding: 6px 12px;
      border-radius: 6px;
      font-size: 11px;
      font-weight: 700;
      cursor: pointer;
      box-shadow: 0 2px 4px rgba(0,0,0,0.1);
      z-index: 100;
    }
    
    .print-button:hover {
      background: #1d4ed8;
    }
    
    .text-success {
      color: #059669;
    }
    
    .text-warning {
      color: #d97706;
    }
    
    .dashed-divider {
      border-top: 1px dashed #cbd5e1;
      margin: 1.5mm 0;
    }
  </style>
</head>
<body>
<div class="receipt-container">
  <div class="header">
    <div class="company-name">Tha ⚡ Ruu</div>
    <div class="store-info">
      Hanwella Road, Kirindiwela | Tel: 94 71 515 9372
    </div>
  </div>

  <div class="invoice-header">
    <div>
      <span class="invoice-title">INVOICE</span>
      ${paymentStatus === 'paid' ? 
        '<span class="status-badge status-paid">✓ PAID</span>' : 
        '<span class="status-badge status-pending">⏳ PENDING</span>'}
      ${isCreditTransaction ? '<span class="payment-badge badge-credit">CREDIT</span>' : ''}
      ${isChequeTransaction ? '<span class="payment-badge badge-cheque">CHEQUE</span>' : ''}
    </div>
    <div class="invoice-number">#${displayNumber}</div>
  </div>

  <div class="customer-info">
    <div class="customer-row-duo">
      <div class="info-group">
        <span class="customer-label">Date:</span>
        <span class="customer-value">${new Date(receiptData.date).toLocaleDateString()}</span>
      </div>
      <div class="info-group">
        <span class="customer-label">Time:</span>
        <span class="customer-value">${new Date(receiptData.date).toLocaleTimeString([], {hour:'2-digit', minute:'2-digit'})}</span>
      </div>
    </div>
    <div class="customer-row-duo" style="margin-bottom: 0;">
      <div class="info-group">
        <span class="customer-label">Customer:</span>
        <span class="customer-value">${receiptData.customer || 'Walk-in Customer'}</span>
      </div>
      <div class="info-group">
        <span class="customer-label">Cashier:</span>
        <span class="customer-value">${localStorage.getItem('erp_username') || 'POS User'}</span>
      </div>
    </div>
  </div>

  ${isChequeTransaction && receiptData.cheque_details ? `
    <div class="payment-details cheque-details">
      <div class="payment-title">🏦 CHEQUE PAYMENT</div>
      <div class="payment-row">
        <span>Cheque No:</span>
        <span><strong>${receiptData.cheque_details.chequeNumber}</strong></span>
      </div>
      <div class="payment-row">
        <span>Bank:</span>
        <span>${receiptData.cheque_details.bankName} ${receiptData.cheque_details.branch ? `(${receiptData.cheque_details.branch})` : ''}</span>
      </div>
      <div class="payment-row">
        <span>Cheque Date:</span>
        <span>${new Date(receiptData.cheque_details.chequeDate).toLocaleDateString()}</span>
      </div>
      <div class="payment-row">
        <span>Due Date:</span>
        <span>${new Date(receiptData.cheque_details.dueDate).toLocaleDateString()}</span>
      </div>
      ${receiptData.cheque_details.isPostDated ? '<div class="payment-row"><span>Type:</span><span>Post-dated</span></div>' : ''}
      <div class="payment-row">
        <span>Status:</span>
        <span class="status-badge status-pending">Pending Clearance</span>
      </div>
    </div>
  ` : ''}

  ${isCreditTransaction ? `
    <div class="payment-details credit-details">
      <div class="payment-title">💰 CREDIT SALE</div>
      <div class="payment-row">
        <span>Due Date:</span>
        <span><strong>${receiptData.due_date || 'N/A'}</strong></span>
      </div>
    </div>
  ` : ''}

  <table class="items-table">
    <thead>
      <tr>
        <th>DESCRIPTION</th>
      </tr>
    </thead>
    <tbody>
      ${regularItems.map(item => {
        const warrantyItem = warrantyItemsWithDates.find(w => w.product_id === item.product_id);
        const serialItem = itemsWithSerials.find(s => s.product_id === item.product_id);
        
        let warrantyDisplay = '';
        if (warrantyItem && warrantyItem.warranty_months > 0) {
          warrantyDisplay = `<div class="warranty-info">🔰 ${warrantyItem.warranty_months}m warranty (until ${warrantyItem.warranty_end_date})</div>`;
          if (warrantyItem.warranty_terms) {
            warrantyDisplay += `<div class="warranty-info">📋 ${warrantyItem.warranty_terms}</div>`;
          }
        }
        
        let serialDisplay = '';
        if (serialItem && serialItem.serial_numbers && serialItem.serial_numbers.length > 0) {
          serialDisplay = `<div class="serial-info">🔢 SN: ${serialItem.serial_numbers.join(', ')}</div>`;
        }
        
        return `
          <tr>
            <td>
              <div class="item-name-large">${item.product_name || item.name}</div>
              ${item.product_code ? `<div style="font-size: 6.5px; color:#64748b; margin-bottom:2px;">Code: ${item.product_code}</div>` : ''}
              <div class="item-meta-row">
                <span class="meta-qty">QTY: ${item.quantity}</span>
                <span class="meta-unit-price">${formatNumber(item.unit_price)}</span>
                <span class="meta-total">₨ ${formatNumber(item.line_total)}</span>
              </div>
              <div class="item-extras">
                ${item.discount_percentage > 0 ? `<div class="discount-info">⚡ Disc ${item.discount_percentage}%</div>` : ''}
                ${warrantyDisplay}
                ${serialDisplay}
              </div>
            </td>
          </tr>
        `;
      }).join('')}
    </tbody>
  </table>

  ${bulkSaleItems.length > 0 ? `
    <div class="bundle-section">
      <div class="bundle-title">📦 BUNDLE PACKAGE</div>
      ${bulkSaleItems.map(item => `
        <div class="bundle-item">
          <span>${item.product_name || item.name}</span>
          <span><strong>x${item.quantity}</strong></span>
        </div>
      `).join('')}
      <div class="bundle-total">
        <span>BUNDLE TOTAL</span>
        <span>LKR ${formatNumber(bulkSaleItems.reduce((sum, item) => sum + (item.line_total || 0), 0))}</span>
      </div>
    </div>
  ` : ''}

  <div class="totals">
    <div class="total-row">
      <span>Subtotal</span>
      <span>${formatNumber(receiptData.subtotal)}</span>
    </div>
    ${safeNumber(receiptData.discount) > 0 ? `
      <div class="total-row text-warning">
        <span>Discount</span>
        <span>-${formatNumber(receiptData.discount)}</span>
      </div>
    ` : ''}
    ${safeNumber(receiptData.tax) > 0 ? `
      <div class="total-row">
        <span>Tax</span>
        <span>${formatNumber(receiptData.tax)}</span>
      </div>
    ` : ''}
    <div class="grand-total">
      <span>TOTAL</span>
      <span>LKR ${formatNumber(receiptData.total)}</span>
    </div>
    <div class="total-row" style="margin-top: 1mm; border-top: 1px solid #cbd5e1; padding-top: 0.8mm;">
      <span>Payment Method</span>
      <span><strong>${formatPaymentMethod(receiptData.payment_method)}</strong></span>
    </div>
    ${!isCreditTransaction && !isChequeTransaction && paymentStatus === 'paid' ? `
      <div class="total-row compact-payment-row">
        <span>Amount Paid</span>
        <span>${formatNumber(receiptData.paid)}</span>
      </div>
      <div class="total-row text-success compact-payment-row">
        <span>Change</span>
        <span>${formatNumber(receiptData.change)}</span>
      </div>
    ` : ''}
  </div>

  <div class="footer">
    <div class="footer-main">Thank you for choosing Tha ⚡ Ruu!</div>
    <div style="font-weight: 600; font-size: 7px;">Your Trusted Electrical Partner</div>
    <div class="footer-services">
      • Retail • Repair • Distribution
    </div>
    <div class="dashed-divider"></div>
    <div class="footer-dev">
      POS System by Jcodelabs.lk | 94 74 164 0774<br>
      Empowering Businesses Through Technology -
      Websites | Custom Systems
          </div>
  </div>

  <button class="print-button no-print" onclick="window.print(); setTimeout(() => window.close(), 1000);">
    🖨️ Print Receipt
  </button>
</div>

<script>
  function formatNumber(value) {
    if (value === undefined || value === null) return "0.00";
    return parseFloat(value).toLocaleString('en-LK', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  
  function safeNumber(val) {
    const n = parseFloat(val);
    return isNaN(n) ? 0 : n;
  }
  
  function formatPaymentMethod(method) {
    if (!method) return "CASH";
    const m = method.toLowerCase();
    if (m === 'cash') return "CASH";
    if (m === 'card') return "CARD";
    if (m === 'cheque') return "CHEQUE";
    if (m === 'credit') return "CREDIT";
    return method.toUpperCase();
  }
</script>
</body>
</html>
`);
    
    receiptWindow.document.close();
  };

  const filteredProducts = products.filter(product =>
    product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    product.product_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (product.category && product.category.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const inactiveProductsCount = products.filter(p => p.is_active === false).length;
  const outOfStockProductsCount = products.filter(p => {
    const goodStock = Math.max(0, p.good_quantity || 0);
    return goodStock <= 0 && p.is_active !== false;
  }).length;

  // Edit Item Dialog Component
  const EditItemDialog = () => {
    const [formData, setFormData] = useState({
      product_name: '',
      quantity: 1,
      unit_price: 0,
      discount_percentage: 0,
      warranty_months: 0,
      warranty_terms: '',
      serial_numbers_input: ''
    });

    useEffect(() => {
      if (currentEditItem) {
        const serialsString = currentEditItem.serial_numbers?.join(', ') || '';
        
        setFormData({
          product_name: currentEditItem.product_name || currentEditItem.name || '',
          quantity: currentEditItem.quantity || 1,
          unit_price: currentEditItem.unit_price || 0,
          discount_percentage: currentEditItem.discount_percentage || 0,
          warranty_months: currentEditItem.warranty_months || 0,
          warranty_terms: currentEditItem.warranty_terms || '',
          serial_numbers_input: serialsString
        });
      }
    }, [currentEditItem]);

    const calculateTotal = () => {
      const quantity = parseFloat(formData.quantity) || 0;
      const unitPrice = parseFloat(formData.unit_price) || 0;
      const discountPercent = parseFloat(formData.discount_percentage) || 0;
      const subtotal = quantity * unitPrice;
      const discountAmount = subtotal * (discountPercent / 100);
      return subtotal - discountAmount;
    };

    const handleSave = () => {
      const serialNumbers = formData.serial_numbers_input
        .split(',')
        .map(sn => sn.trim())
        .filter(sn => sn !== '');

      if (currentEditItem.track_serials && serialNumbers.length !== parseInt(formData.quantity)) {
        setError(`Serial numbers count (${serialNumbers.length}) must match quantity (${formData.quantity})`);
        setTimeout(() => setError(''), 3000);
        return;
      }

      const updatedItem = {
        ...currentEditItem,
        name: formData.product_name.trim() || currentEditItem.name,
        product_name: formData.product_name.trim() || currentEditItem.name,
        quantity: parseInt(formData.quantity) || 1,
        unit_price: parseFloat(formData.unit_price) || 0,
        discount_percentage: parseFloat(formData.discount_percentage) || 0,
        line_total: calculateTotal(),
        warranty_months: parseInt(formData.warranty_months) || 0,
        warranty_terms: formData.warranty_terms.trim(),
        serial_numbers: serialNumbers
      };

      updateCartItem(updatedItem);
    };

    if (!currentEditItem) return null;

    return (
      <Modal
        open={editItemDialogOpen}
        onClose={() => setEditItemDialogOpen(false)}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{ timeout: 500 }}
      >
        <Fade in={editItemDialogOpen}>
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 600,
            maxWidth: '90vw',
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 4,
            borderRadius: 2
          }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
              <Typography variant="h6">Edit Item</Typography>
              <IconButton onClick={() => setEditItemDialogOpen(false)}>
                <CloseIcon />
              </IconButton>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Product Name"
                  value={formData.product_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, product_name: e.target.value }))}
                />
              </Grid>
              
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Quantity"
                  type="number"
                  value={formData.quantity}
                  onChange={(e) => setFormData(prev => ({ ...prev, quantity: e.target.value }))}
                  inputProps={{ min: 1, max: currentEditItem.good_stock }}
                  helperText={`Good Stock: ${currentEditItem.good_stock}`}
                />
              </Grid>
              
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Unit Price (LKR)"
                  type="number"
                  value={formData.unit_price}
                  onChange={(e) => setFormData(prev => ({ ...prev, unit_price: e.target.value }))}
                  InputProps={{ startAdornment: <InputAdornment position="start">LKR</InputAdornment> }}
                />
              </Grid>
              
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Discount %"
                  type="number"
                  value={formData.discount_percentage}
                  onChange={(e) => setFormData(prev => ({ ...prev, discount_percentage: e.target.value }))}
                  InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                  <TextField
                    sx={{ width: '30%' }}
                    label="Warranty (Months)"
                    type="number"
                    value={formData.warranty_months}
                    onChange={(e) => setFormData(prev => ({ ...prev, warranty_months: e.target.value }))}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">months</InputAdornment>,
                    }}
                  />
                  <TextField
                    sx={{ width: '70%' }}
                    label="Warranty Terms"
                    multiline
                    rows={1}
                    value={formData.warranty_terms}
                    onChange={(e) => setFormData(prev => ({ ...prev, warranty_terms: e.target.value }))}
                    placeholder="e.g., Parts & Labor, Carry-in, On-site"
                  />
                </Box>
              </Grid>
              
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <SerialIcon color="primary" fontSize="small" />
                  <Typography variant="subtitle2" color="primary">
                    Serial Numbers {formData.quantity > 1 ? `(Need ${formData.quantity} serial numbers)` : ''}
                  </Typography>
                </Box>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  value={formData.serial_numbers_input}
                  onChange={(e) => setFormData(prev => ({ ...prev, serial_numbers_input: e.target.value }))}
                  placeholder={formData.quantity > 1 
                    ? `Enter ${formData.quantity} serial numbers separated by commas (e.g., SN001, SN002, SN003)` 
                    : "Enter serial number (e.g., SN001)"}
                  helperText={`${formData.serial_numbers_input.split(',').filter(sn => sn.trim() !== '').length} of ${formData.quantity} serial numbers entered`}
                  error={formData.serial_numbers_input.split(',').filter(sn => sn.trim() !== '').length > 0 && 
                         formData.serial_numbers_input.split(',').filter(sn => sn.trim() !== '').length !== parseInt(formData.quantity)}
                  InputProps={{
                    readOnly: currentEditItem.track_serials && formData.serial_numbers_input ? true : false
                  }}
                  sx={currentEditItem.track_serials && formData.serial_numbers_input ? { bgcolor: '#f5f5f5' } : {}}
                />
                {currentEditItem.track_serials && formData.serial_numbers_input && (
                  <Typography variant="caption" color="success.main">
                    ✓ Serial numbers pre-selected from available stock
                  </Typography>
                )}
              </Grid>
              
              <Grid item xs={12}>
                <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, mt: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Item Total: LKR {calculateTotal().toFixed(2)}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {formData.quantity} × LKR {formData.unit_price} 
                    {formData.discount_percentage > 0 ? ` - ${formData.discount_percentage}% discount` : ''}
                  </Typography>
                  
                  {(formData.warranty_months > 0 || formData.warranty_terms || formData.serial_numbers_input) && (
                    <Box sx={{ mt: 2, pt: 2, borderTop: '1px dashed #ccc' }}>
                      {formData.warranty_months > 0 && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <WarrantyIcon fontSize="small" sx={{ color: '#2e7d32' }} />
                          <Typography variant="body2" color="success.main">
                            <strong>{formData.warranty_months} months warranty</strong>
                            {formData.warranty_terms && ` - ${formData.warranty_terms}`}
                          </Typography>
                        </Box>
                      )}
                      
                      {formData.serial_numbers_input && (
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                          <SerialIcon fontSize="small" sx={{ color: '#1976d2', mt: 0.5 }} />
                          <Box>
                            <Typography variant="body2" color="info.main">
                              <strong>Serial Numbers:</strong>
                            </Typography>
                            <Typography variant="body2" color="info.main" sx={{ fontFamily: 'monospace' }}>
                              {formData.serial_numbers_input.split(',').map(sn => sn.trim()).filter(sn => sn).join(' • ')}
                            </Typography>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  )}
                </Box>
              </Grid>
            </Grid>

            <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button onClick={() => setEditItemDialogOpen(false)} variant="outlined">
                Cancel
              </Button>
              <Button 
                onClick={handleSave} 
                variant="contained" 
                startIcon={<SaveIcon />}
                disabled={currentEditItem.track_serials && 
                         formData.serial_numbers_input.split(',').filter(sn => sn.trim() !== '').length !== parseInt(formData.quantity)}
              >
                Save Item
              </Button>
            </Box>
          </Box>
        </Fade>
      </Modal>
    );
  };

  // Bulk Sale Dialog Component - FIXED
  const BulkSaleDialog = () => {
    const [localSelectedProducts, setLocalSelectedProducts] = useState([]);
    const [localBulkSalePrice, setLocalBulkSalePrice] = useState('');
    const [localSearchTerm, setLocalSearchTerm] = useState('');

    useEffect(() => {
      if (bulkSaleDialogOpen) {
        setLocalSelectedProducts(selectedBulkProducts);
        setLocalBulkSalePrice(bulkSalePrice);
        setLocalSearchTerm(searchBulkTerm);
      }
    }, [bulkSaleDialogOpen, selectedBulkProducts, bulkSalePrice, searchBulkTerm]);

    const filteredBulkProducts = products.filter(product =>
      product.name.toLowerCase().includes(localSearchTerm.toLowerCase()) ||
      product.product_code.toLowerCase().includes(localSearchTerm.toLowerCase())
    );

    const handleAddToBulkSaleLocal = (product) => {
      if (product.is_active === false) {
        setError('Cannot add inactive product to bulk sale');
        return;
      }
      const availableGoodStock = Math.max(0, product.good_quantity || 0);
      if (availableGoodStock <= 0) {
        setError('No good stock available for this product');
        return;
      }
      setLocalSelectedProducts(prev => {
        const existing = prev.find(p => p.id === product.id);
        if (existing) {
          if (existing.quantity + 1 > availableGoodStock) {
            setError(`Only ${availableGoodStock} good items available in stock for ${product.name}`);
            setTimeout(() => setError(''), 3000);
            return prev;
          }
          return prev.map(p =>
            p.id === product.id
              ? { ...p, quantity: p.quantity + 1, line_total: p.price * (p.quantity + 1), selected_serials: [] }
              : p
          );
        } else {
          return [...prev, {
            ...product,
            quantity: 1,
            line_total: product.price,
            good_stock: availableGoodStock,
            selected_serials: []
          }];
        }
      });
    };

    const updateBulkQuantityLocal = (id, delta) => {
      setLocalSelectedProducts(prev =>
        prev.map(item => {
          if (item.id === id) {
            const newQuantity = item.quantity + delta;
            if (newQuantity < 1) return null;
            
            const product = products.find(p => p.id === item.id);
            const availableGoodStock = Math.max(0, product?.good_quantity || 0);
            
            if (newQuantity > availableGoodStock) {
              setError(`Only ${availableGoodStock} good items available in stock for ${item.name}`);
              setTimeout(() => setError(''), 3000);
              return item;
            }
            
            // Clear selected serials if quantity changed
            const selectedSerials = (delta !== 0 && item.track_serials) ? [] : item.selected_serials;
            
            return {
              ...item,
              quantity: newQuantity,
              line_total: item.price * newQuantity,
              selected_serials: selectedSerials
            };
          }
          return item;
        }).filter(item => item !== null)
      );
    };

    const removeFromBulkSaleLocal = (id) => {
      setLocalSelectedProducts(prev => prev.filter(item => item.id !== id));
    };

    const calculateBulkTotalLocal = () => {
      return localSelectedProducts.reduce((sum, item) => sum + item.line_total, 0);
    };

    const handleConfirmBulkSaleLocal = () => {
      if (localSelectedProducts.length === 0) {
        setError('Please add products to bulk sale');
        return;
      }
      if (!localBulkSalePrice || parseFloat(localBulkSalePrice) <= 0) {
        setError('Please enter a valid bulk sale price');
        return;
      }

      setSelectedBulkProducts(localSelectedProducts);
      setBulkSalePrice(localBulkSalePrice);
      
      // Store the bulk products and price
      setPendingBulkProducts(localSelectedProducts);
      setPendingBulkPrice(localBulkSalePrice);
      setPendingBulkSerials([]);
      setPendingBulkProductIndex(0);

      // Find first product that needs serials
      const productsNeedingSerials = localSelectedProducts.filter(p => 
        p.track_serials && (!p.selected_serials || p.selected_serials.length < p.quantity)
      );
      
      if (productsNeedingSerials.length > 0) {
        // Close the bulk dialog and start serial selection
        setBulkSaleDialogOpen(false);
        const firstProduct = productsNeedingSerials[0];
        setPendingBulkProduct(firstProduct);
        setPendingBulkQuantity(firstProduct.quantity);
        setBulkSerialSelectionOpen(true);
      } else {
        // No serials needed, process directly
        processBulkSale(localSelectedProducts, localBulkSalePrice);
        setBulkSaleDialogOpen(false);
      }
    };

    const handleClose = () => {
      setSelectedBulkProducts([]);
      setBulkSalePrice('');
      setSearchBulkTerm('');
      setBulkSaleDialogOpen(false);
    };

    return (
      <Dialog open={bulkSaleDialogOpen} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <LocalOfferIcon color="secondary" />
            <Typography variant="h6">Bulk Sale</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2, height: '100%' }}>
                <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                  Select Products
                </Typography>
                <TextField
                  fullWidth
                  placeholder="Search products..."
                  value={localSearchTerm}
                  onChange={(e) => setLocalSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ mb: 2 }}
                />
                <Box sx={{ height: '400px', overflowY: 'auto', border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}>
                  {filteredBulkProducts.map(product => {
                    const isSelected = localSelectedProducts.some(p => p.id === product.id);
                    const selectedProduct = isSelected ? localSelectedProducts.find(p => p.id === product.id) : null;
                    const isActive = product.is_active !== false;
                    const availableGoodStock = Math.max(0, product.good_quantity || 0);
                    const hasGoodStock = availableGoodStock > 0;
                    
                    return (
                      <Paper
                        key={product.id}
                        sx={{
                          p: 2,
                          mb: 1,
                          cursor: isActive && hasGoodStock ? 'pointer' : 'default',
                          border: isSelected ? '2px solid #1976d2' : '1px solid #e0e0e0',
                          opacity: (!isActive || !hasGoodStock) ? 0.7 : 1,
                          position: 'relative'
                        }}
                        onClick={() => {
                          if (isActive && hasGoodStock) {
                            handleAddToBulkSaleLocal(product);
                          }
                        }}
                      >
                        {!isActive && (
                          <Chip
                            label="INACTIVE"
                            size="small"
                            color="warning"
                            icon={<InactiveIcon />}
                            sx={{ position: 'absolute', top: 5, right: 5, height: 20, fontSize: '0.7rem' }}
                          />
                        )}
                        {isActive && !hasGoodStock && (
                          <Chip
                            label="NO GOOD STOCK"
                            size="small"
                            color="error"
                            icon={<WarningIcon />}
                            sx={{ position: 'absolute', top: 5, right: 5, height: 20, fontSize: '0.7rem' }}
                          />
                        )}
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {product.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Code: {product.product_code}
                            </Typography>
                            {product.warranty_months > 0 && (
                              <Chip
                                label={`${product.warranty_months}m Warranty`}
                                size="small"
                                color="info"
                                sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                                icon={<WarrantyIcon fontSize="small" />}
                              />
                            )}
                            {product.track_serials && (
                              <Chip
                                label="Has Serials"
                                size="small"
                                color="primary"
                                sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                                icon={<SerialIcon fontSize="small" />}
                              />
                            )}
                          </Box>
                          <Typography variant="body2" fontWeight="bold" color="primary">
                            LKR {product.price.toFixed(2)}
                          </Typography>
                        </Box>
                        <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="caption" color={availableGoodStock <= (product.reorder_level || 10) ? 'warning.main' : 'text.secondary'}>
                            Good Stock: {availableGoodStock}
                          </Typography>
                          {isSelected && selectedProduct && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  updateBulkQuantityLocal(product.id, -1);
                                }}
                              >
                                <RemoveIcon fontSize="small" />
                              </IconButton>
                              <Typography variant="body2">
                                Qty: {selectedProduct.quantity}
                              </Typography>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  updateBulkQuantityLocal(product.id, 1);
                                }}
                              >
                                <AddIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          )}
                        </Box>
                      </Paper>
                    );
                  })}
                </Box>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2, height: '100%' }}>
                <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                  Bulk Sale Summary
                </Typography>
                {localSelectedProducts.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 8 }}>
                    <LocalOfferIcon sx={{ fontSize: 60, color: 'grey.300', mb: 2 }} />
                    <Typography color="text.secondary">No products selected</Typography>
                  </Box>
                ) : (
                  <>
                    <TableContainer sx={{ maxHeight: 300, mb: 2 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Product</TableCell>
                            <TableCell align="center">Qty</TableCell>
                            <TableCell align="right">Price</TableCell>
                            <TableCell align="center">Action</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {localSelectedProducts.map(item => (
                            <TableRow key={item.id}>
                              <TableCell>
                                <Typography variant="body2">{item.name}</Typography>
                                {item.warranty_months > 0 && (
                                  <Chip
                                    label={`${item.warranty_months}m`}
                                    size="small"
                                    color="info"
                                    sx={{ height: 18, fontSize: '0.65rem', mt: 0.5 }}
                                    icon={<WarrantyIcon fontSize="small" />}
                                  />
                                )}
                                {item.track_serials && (
                                  <Chip
                                    label="Serials"
                                    size="small"
                                    color="primary"
                                    sx={{ height: 18, fontSize: '0.65rem', mt: 0.5, ml: 0.5 }}
                                    icon={<SerialIcon fontSize="small" />}
                                  />
                                )}
                              </TableCell>
                              <TableCell align="center">
                                <Box display="flex" alignItems="center" justifyContent="center">
                                  <IconButton
                                    size="small"
                                    onClick={() => updateBulkQuantityLocal(item.id, -1)}
                                  >
                                    <RemoveIcon fontSize="small" />
                                  </IconButton>
                                  <Typography sx={{ mx: 1, minWidth: 20 }}>
                                    {item.quantity}
                                  </Typography>
                                  <IconButton
                                    size="small"
                                    onClick={() => updateBulkQuantityLocal(item.id, 1)}
                                  >
                                    <AddIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2">
                                  LKR {item.line_total.toFixed(2)}
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                  @ LKR {item.price.toFixed(2)}
                                </Typography>
                              </TableCell>
                              <TableCell align="center">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => removeFromBulkSaleLocal(item.id)}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    <Divider sx={{ my: 2 }} />
                    <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, mb: 2 }}>
                      <Grid container spacing={1}>
                        <Grid item xs={6}>
                          <Typography variant="body2">Total Items:</Typography>
                        </Grid>
                        <Grid item xs={6} textAlign="right">
                          <Typography variant="body2" fontWeight="bold">
                            {localSelectedProducts.reduce((sum, item) => sum + item.quantity, 0)}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2">Products:</Typography>
                        </Grid>
                        <Grid item xs={6} textAlign="right">
                          <Typography variant="body2" fontWeight="bold">
                            {localSelectedProducts.length}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2">Original Total:</Typography>
                        </Grid>
                        <Grid item xs={6} textAlign="right">
                          <Typography variant="body2" fontWeight="bold">
                            LKR {calculateBulkTotalLocal().toFixed(2)}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Box>
                    <TextField
                      fullWidth
                      label="Bulk Sale Price (LKR)"
                      type="number"
                      value={localBulkSalePrice}
                      onChange={(e) => setLocalBulkSalePrice(e.target.value)}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">LKR</InputAdornment>,
                      }}
                      sx={{ mb: 2 }}
                    />
                    <Alert severity="info" sx={{ mt: 1 }}>
                      <Typography variant="caption">
                        Discount: {calculateBulkTotalLocal() > 0 && localBulkSalePrice ? 
                          `${((1 - parseFloat(localBulkSalePrice) / calculateBulkTotalLocal()) * 100).toFixed(2)}%` : 
                          '0%'}
                      </Typography>
                    </Alert>
                    {localSelectedProducts.some(p => p.track_serials) && (
                      <Alert severity="warning" sx={{ mt: 2 }}>
                        <Typography variant="body2">
                          <SerialIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                          This bulk sale contains products with serial numbers. You will need to select serial numbers for each product.
                        </Typography>
                      </Alert>
                    )}
                  </>
                )}
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmBulkSaleLocal}
            variant="contained"
            color="secondary"
            disabled={localSelectedProducts.length === 0 || !localBulkSalePrice || parseFloat(localBulkSalePrice) <= 0}
            startIcon={<LocalOfferIcon />}
          >
            Add to Cart ({localSelectedProducts.reduce((sum, item) => sum + item.quantity, 0)} items)
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  const creditCustomers = customers.filter(c => c.is_credit_customer);

  return (
    <Container maxWidth="xl" sx={{ mt: 2, mb: 4 }}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h5" component="h1">
                Point of Sale
              </Typography>
              <Box display="flex" gap={1}>
                <Tooltip title={`${inactiveProductsCount} inactive products`}>
                  <Button
                    variant="outlined"
                    color="warning"
                    startIcon={<InactiveIcon />}
                    onClick={() => {
                      setSearchTerm('');
                      const inactiveProducts = products.filter(p => p.is_active === false);
                      if (inactiveProducts.length > 0) {
                        setError(`${inactiveProducts.length} inactive products found. They cannot be added to cart.`);
                      }
                    }}
                  >
                    Inactive ({inactiveProductsCount})
                  </Button>
                </Tooltip>
                <Tooltip title={`${outOfStockProductsCount} products with no good stock`}>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<WarningIcon />}
                    onClick={() => {
                      setSearchTerm('');
                      const outOfStockProducts = products.filter(p => {
                        const goodStock = Math.max(0, p.good_quantity || 0);
                        return goodStock <= 0 && p.is_active !== false;
                      });
                      if (outOfStockProducts.length > 0) {
                        setError(`${outOfStockProducts.length} products with no good stock found. They cannot be added to cart.`);
                      }
                    }}
                  >
                    No Good Stock ({outOfStockProductsCount})
                  </Button>
                </Tooltip>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={fetchProducts}
                >
                  Refresh
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<ArrowBackIcon />}
                  onClick={() => navigate('/sales')}
                >
                  Back
                </Button>
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<LocalOfferIcon />}
                  onClick={() => setBulkSaleDialogOpen(true)}
                >
                  Bulk Sale
                </Button>
              </Box>
            </Box>

            {/* Product Status Summary */}
            {(inactiveProductsCount > 0 || outOfStockProductsCount > 0) && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Box display="flex" alignItems="center" gap={2}>
                  {inactiveProductsCount > 0 && (
                    <Chip
                      icon={<InactiveIcon />}
                      label={`${inactiveProductsCount} Inactive Products`}
                      size="small"
                      color="warning"
                    />
                  )}
                  {outOfStockProductsCount > 0 && (
                    <Chip
                      icon={<WarningIcon />}
                      label={`${outOfStockProductsCount} No Good Stock`}
                      size="small"
                      color="error"
                    />
                  )}
                  <Typography variant="caption" color="textSecondary">
                    Inactive and out of stock products cannot be added to cart
                  </Typography>
                </Box>
              </Alert>
            )}

            {/* Customer Selection */}
            <Paper sx={{ p: 2, mb: 3, bgcolor: selectedCustomer.is_credit_customer ? '#e8f4fd' : 'primary.light', color: selectedCustomer.is_credit_customer ? '#007bff' : 'white' }}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="subtitle1">
                      Customer: <strong>{selectedCustomer.name}</strong>
                    </Typography>
                    {selectedCustomer.is_credit_customer && (
                      <Chip
                        label="CREDIT"
                        size="small"
                        color="primary"
                        icon={<CreditCardIcon />}
                        sx={{ height: 20 }}
                      />
                    )}
                  </Box>
                  <Typography variant="body2">
                    {selectedCustomer.phone || 'No phone'}
                  </Typography>
                  {selectedCustomer.is_credit_customer && (
                    <Box display="flex" gap={2} sx={{ mt: 1 }}>
                      <Typography variant="caption">
                        Credit Limit: {formatCurrency(selectedCustomer.credit_limit)}
                      </Typography>
                      <Typography variant="caption">
                        Available: {formatCurrency(selectedCustomer.available_credit)}
                      </Typography>
                    </Box>
                  )}
                </Box>
                <Box display="flex" gap={1} alignItems="center">
                  <Autocomplete
                    sx={{ width: 300 }}
                    options={customers}
                    getOptionLabel={(option) => `${option.name} ${option.is_credit_customer ? '💳' : ''} (${option.phone || 'No phone'})`}
                    value={selectedCustomer.id !== 1 ? customers.find(c => c.id === selectedCustomer.id) : null}
                    onChange={(event, newValue) => {
                      if (newValue) {
                        setSelectedCustomer({
                          id: newValue.id,
                          name: newValue.name,
                          phone: newValue.phone,
                          is_credit_customer: newValue.is_credit_customer || false,
                          credit_limit: newValue.credit_limit || 0,
                          available_credit: newValue.available_credit || 0
                        });
                      } else {
                        setSelectedCustomer({
                          id: 1,
                          name: 'Walk-in Customer',
                          is_credit_customer: false,
                          credit_limit: 0,
                          available_credit: 0
                        });
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Search Customer"
                        size="small"
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon />
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                  />
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<PersonAddIcon />}
                    onClick={() => setCustomerDialogOpen(true)}
                  >
                    New Customer
                  </Button>
                </Box>
              </Box>
            </Paper>

            {/* Stats Cards */}
            <Grid container spacing={2} mb={3}>
              <Grid item xs={6} sm={3}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" variant="body2">
                      Today's Sales
                    </Typography>
                    <Typography variant="h6">
                      {stats.today?.sales || '0.00'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" variant="body2">
                      Today's Transactions
                    </Typography>
                    <Typography variant="h6">
                      {stats.today?.transactions || 0}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" variant="body2">
                      Monthly Sales
                    </Typography>
                    <Typography variant="h6">
                      {stats.monthly?.sales || '0.00'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" variant="body2">
                      Cart Items
                    </Typography>
                    <Typography variant="h6">
                      {cart.length}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Search Input */}
            <TextField
              fullWidth
              placeholder="Scan barcode or search products..."
              value={searchTerm}
              onChange={handleSearch}
              onKeyPress={handleBarcodeKeyPress}
              inputRef={barcodeInputRef}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 3 }}
            />

            {/* Products Grid */}
            <Box sx={{ height: 'calc(100vh - 500px)', minHeight: '400px', overflowY: 'auto', pr: 1 }}>
              <Grid container spacing={2}>
                {filteredProducts.map((product) => {
                  const isActive = product.is_active !== false;
                  const availableGoodStock = Math.max(0, product.good_quantity || 0);
                  const hasGoodStock = availableGoodStock > 0;
                  const hasSerials = product.track_serials;
                  
                  return (
                    <Grid item xs={6} sm={4} md={3} key={product.id}>
                      <Tooltip title={
                        !isActive ? 'Product is inactive - Cannot add to cart' :
                        !hasGoodStock ? `No good stock available - Good stock: ${availableGoodStock}` :
                        hasSerials ? `Available: ${availableGoodStock} good units | Has serial numbers` :
                        `Available: ${availableGoodStock} good units | Price: LKR ${product.price.toFixed(2)}`
                      }>
                        <Paper
                          sx={{
                            p: 2,
                            cursor: isActive && hasGoodStock ? 'pointer' : 'default',
                            border: !hasGoodStock ? '2px solid #ff6b6b' : !isActive ? '2px solid #ff9800' : hasSerials ? '2px solid #1976d2' : '1px solid #e0e0e0',
                            position: 'relative',
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between',
                            opacity: (!isActive || !hasGoodStock) ? 0.7 : 1
                          }}
                          onClick={() => {
                            if (isActive && hasGoodStock) {
                              addToCart(product, 1);
                            }
                          }}
                        >
                          {!isActive && (
                            <Box sx={{ position: 'absolute', top: 0, right: 0, bgcolor: 'warning.main', color: 'white', px: 1, py: 0.2, fontSize: '0.7rem', borderBottomLeftRadius: 4 }}>
                              INACTIVE
                            </Box>
                          )}
                          {isActive && !hasGoodStock && (
                            <Box sx={{ position: 'absolute', top: 0, right: 0, bgcolor: 'error.main', color: 'white', px: 1, py: 0.2, fontSize: '0.7rem', borderBottomLeftRadius: 4 }}>
                              NO GOOD STOCK
                            </Box>
                          )}
                          {isActive && hasGoodStock && hasSerials && (
                            <Box sx={{ position: 'absolute', top: 0, left: 0, bgcolor: 'primary.main', color: 'white', px: 1, py: 0.2, fontSize: '0.7rem', borderBottomRightRadius: 4 }}>
                              <SerialIcon fontSize="inherit" sx={{ mr: 0.3 }} /> SERIALS
                            </Box>
                          )}
                          <Box>
                            <Typography variant="subtitle2" noWrap>
                              {product.name}
                            </Typography>
                            <Typography variant="body2" color="textSecondary" noWrap>
                              Code: {product.product_code}
                            </Typography>
                            <Typography variant="body2" fontWeight="bold" color="primary">
                              LKR {product.price.toFixed(2)}
                            </Typography>
                          </Box>
                          <Box>
                            {product.warranty_months > 0 && (
                              <Tooltip title={product.warranty_terms || `${product.warranty_months} months warranty`}>
                                <Chip
                                  icon={<WarrantyIcon fontSize="small" />}
                                  label={`${product.warranty_months}m Warranty`}
                                  size="small"
                                  color="info"
                                  sx={{ mt: 0.5, fontSize: '0.7rem', height: 20 }}
                                />
                              </Tooltip>
                            )}
                            <Box sx={{ display: 'flex', gap: 0.5, mt: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                              <Chip
                                label={`Good: ${availableGoodStock}`}
                                size="small"
                                color={availableGoodStock <= (product.reorder_level || 10) ? 'warning' : 'success'}
                                sx={{ fontSize: '0.7rem', height: 20 }}
                              />
                              {(product.issue_quantity || 0) > 0 && (
                                <Chip
                                  icon={<WarningIcon fontSize="small" />}
                                  label={`Issues: ${product.issue_quantity}`}
                                  size="small"
                                  color="error"
                                  variant="outlined"
                                  sx={{ fontSize: '0.7rem', height: 20 }}
                                />
                              )}
                              {product.quantity > availableGoodStock && (
                                <Tooltip title={`Total stock: ${product.quantity}`}>
                                  <Chip
                                    label={`Total: ${product.quantity}`}
                                    size="small"
                                    variant="outlined"
                                    sx={{ fontSize: '0.7rem', height: 20 }}
                                  />
                                </Tooltip>
                              )}
                            </Box>
                          </Box>
                        </Paper>
                      </Tooltip>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          </Paper>
        </Grid>

        {/* Cart Column */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">
                Cart ({cart.length} items)
              </Typography>
              <Badge badgeContent={cart.length} color="primary">
                <ReceiptIcon />
              </Badge>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                {error}
              </Alert>
            )}
            {success && (
              <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
                {success}
              </Alert>
            )}

            {/* Cart Items Table */}
            <TableContainer sx={{ flex: 1, mb: 2, maxHeight: 300, overflowY: 'auto' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Item</TableCell>
                    <TableCell align="center">Qty</TableCell>
                    <TableCell align="right">Price</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {cart.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                        <Typography color="textSecondary">Cart is empty</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    cart.map((item) => (
                      <TableRow key={item.id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {item.name}
                          </Typography>
                          <Box display="flex" flexWrap="wrap" gap={0.5} sx={{ mt: 0.5 }}>
                            {item.is_bulk_sale && (
                              <Chip label="Bulk" size="small" color="secondary" sx={{ height: 20, fontSize: '0.7rem' }} />
                            )}
                            {item.discount_percentage > 0 && (
                              <Chip label={`${item.discount_percentage}%`} size="small" color="success" sx={{ height: 20, fontSize: '0.7rem' }} />
                            )}
                            {item.warranty_months > 0 && (
                              <Tooltip title={item.warranty_terms || `${item.warranty_months} months warranty`}>
                                <Chip 
                                  icon={<WarrantyIcon fontSize="small" />}
                                  label={`${item.warranty_months}m`} 
                                  size="small" 
                                  color="info" 
                                  sx={{ height: 20, fontSize: '0.7rem' }} 
                                />
                              </Tooltip>
                            )}
                            {item.serial_numbers && item.serial_numbers.length > 0 && (
                              <Tooltip title={`Serial: ${item.serial_numbers.join(', ')}`}>
                                <Chip 
                                  icon={<SerialIcon fontSize="small" />}
                                  label={`${item.serial_numbers.length} SN`} 
                                  size="small" 
                                  color="primary" 
                                  sx={{ height: 20, fontSize: '0.7rem' }} 
                                />
                              </Tooltip>
                            )}
                            {item.track_serials && (!item.serial_numbers || item.serial_numbers.length === 0) && (
                              <Chip 
                                icon={<WarningIcon fontSize="small" />}
                                label="No Serial" 
                                size="small" 
                                color="warning" 
                                sx={{ height: 20, fontSize: '0.7rem' }} 
                              />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Box display="flex" alignItems="center" justifyContent="center">
                            <IconButton size="small" onClick={() => updateQuantity(item.id, -1)}>
                              <RemoveIcon fontSize="small" />
                            </IconButton>
                            <Typography variant="body2" sx={{ mx: 1, minWidth: 30 }}>
                              {item.quantity}
                            </Typography>
                            <IconButton size="small" onClick={() => updateQuantity(item.id, 1)}>
                              <AddIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">
                            LKR {item.line_total.toFixed(2)}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            LKR {item.unit_price.toFixed(2)} each
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <IconButton size="small" onClick={() => editItem(item)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" color="error" onClick={() => removeFromCart(item.id)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Divider sx={{ my: 2 }} />

            {/* Totals */}
            <List dense>
              <ListItem>
                <ListItemText primary="Subtotal" />
                <Typography fontWeight="bold">LKR {subtotal.toFixed(2)}</Typography>
              </ListItem>
              <ListItem>
                <ListItemText
                  primary={
                    <Box display="flex" alignItems="center" gap={1}>
                      <LocalOfferIcon fontSize="small" />
                      <Typography>Discount</Typography>
                    </Box>
                  }
                  secondary={
                    <Box sx={{ mt: 1 }}>
                      <TextField
                        select
                        size="small"
                        value={discount.type}
                        onChange={(e) => setDiscount(prev => ({ ...prev, type: e.target.value }))}
                        sx={{ width: 100, mr: 1 }}
                      >
                        <MenuItem value="percentage">%</MenuItem>
                        <MenuItem value="fixed">LKR</MenuItem>
                      </TextField>
                      <TextField
                        size="small"
                        type="number"
                        value={discount.value}
                        onChange={(e) => setDiscount(prev => ({ ...prev, value: parseFloat(e.target.value) || 0 }))}
                        sx={{ width: 100 }}
                      />
                    </Box>
                  }
                />
                <Typography color="error">- LKR {discountAmount.toFixed(2)}</Typography>
              </ListItem>
              <ListItem>
                <Box display="flex" alignItems="center" width="100%">
                  <Typography variant="body2" sx={{ flex: 1 }}>
                    Tax Rate (%)
                  </Typography>
                  <TextField
                    size="small"
                    type="number"
                    value={taxRate}
                    onChange={(e) => setTaxRate(parseFloat(e.target.value) || 0)}
                    sx={{ width: 80 }}
                  />
                </Box>
              </ListItem>
              <ListItem>
                <ListItemText primary="Tax Amount" />
                <Typography>LKR {taxAmount.toFixed(2)}</Typography>
              </ListItem>
              <Divider />
              <ListItem sx={{ backgroundColor: 'primary.main', color: 'white', borderRadius: 1 }}>
                <ListItemText primary={<Typography variant="h6">TOTAL</Typography>} />
                <Typography variant="h6">LKR {totalAmount.toFixed(2)}</Typography>
              </ListItem>
            </List>

            {/* Action Buttons */}
            <Box sx={{ mt: 'auto', pt: 2 }}>
              <Button
                fullWidth
                variant="contained"
                size="large"
                startIcon={<PaymentIcon />}
                disabled={cart.length === 0 || loading}
                onClick={() => setPaymentDialogOpen(true)}
                sx={{ mb: 1, py: 1.5 }}
              >
                {loading ? <CircularProgress size={24} /> : 'PROCESS PAYMENT'}
              </Button>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<CancelIcon />}
                onClick={() => setCart([])}
                disabled={cart.length === 0}
              >
                Clear Cart
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Quantity Dialog */}
      <QuantityDialog
        open={quantityDialogOpen}
        onClose={() => {
          setQuantityDialogOpen(false);
          setSelectedProductForQuantity(null);
        }}
        product={selectedProductForQuantity}
        onConfirm={handleQuantityConfirm}
        availableStock={selectedProductForQuantity ? Math.max(0, selectedProductForQuantity.good_quantity || 0) : 0}
      />

      {/* Serial Selection Modal */}
      <SerialSelectionModal
        open={serialSelectionOpen}
        onClose={() => {
          setSerialSelectionOpen(false);
          setPendingProduct(null);
        }}
        product={pendingProduct}
        onSelect={handleSerialSelection}
        quantity={pendingQuantity}
      />

      {/* Bulk Serial Selection Modal */}
      <BulkSerialSelectionModal
        open={bulkSerialSelectionOpen}
        onClose={() => {
          setBulkSerialSelectionOpen(false);
          setPendingBulkProduct(null);
        }}
        product={pendingBulkProduct}
        onSelect={handleBulkSerialSelection}
        quantity={pendingBulkQuantity}
      />

      {/* Payment Status Modal */}
      <PaymentStatusModal
        open={paymentStatusModalOpen}
        onClose={() => {
          setPaymentStatusModalOpen(false);
          setPendingReceiptData(null);
        }}
        onConfirm={handlePaymentStatusConfirm}
        transactionData={pendingReceiptData || {}}
      />

      {/* Credit Payment Modal */}
      {selectedCreditCustomer && (
        <CreditPaymentModal
          open={creditPaymentOpen}
          onClose={() => {
            setCreditPaymentOpen(false);
            setSelectedCreditCustomer(null);
          }}
          customer={selectedCreditCustomer}
          amount={totalAmount}
          onSuccess={handleCreditPaymentSuccess}
          onCreateCreditSale={createCreditSale}
          invoiceNumber={invoiceNumber}
        />
      )}

      {/* Cheque Payment Modal */}
      <ChequePaymentModal
        open={chequePaymentOpen}
        onClose={() => {
          setChequePaymentOpen(false);
          setPayment(prev => ({ ...prev, method: 'cash', amountPaid: '', change: 0, chequeDetails: null }));
        }}
        amount={totalAmount}
        customer={selectedCustomer}
        onProcessChequePayment={handleProcessChequePayment}
        invoiceNumber={invoiceNumber}
      />

      {/* Payment Dialog */}
      <Dialog open={paymentDialogOpen} onClose={() => setPaymentDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Complete Payment</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Payment Method"
                  select
                  value={payment.method}
                  onChange={(e) => handlePaymentChange('method', e.target.value)}
                >
                  <MenuItem value="cash">Cash</MenuItem>
                  <MenuItem value="card">Credit/Debit Card</MenuItem>
                  <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
                  <MenuItem value="online">Online Payment</MenuItem>
                  <MenuItem value="cheque">
                    <Box display="flex" alignItems="center" gap={1}>
                      <ChequeIcon fontSize="small" />
                      <span>Cheque</span>
                    </Box>
                  </MenuItem>
                  <MenuItem
                    value="credit"
                    disabled={!selectedCustomer.is_credit_customer || selectedCustomer.available_credit < totalAmount}
                  >
                    Credit {!selectedCustomer.is_credit_customer && '(Not available)'}
                  </MenuItem>
                </TextField>
              </Grid>

              {payment.method !== 'credit' && payment.method !== 'cheque' && (
                <>
                  <Grid item xs={12}>
                    <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                      <Typography variant="body2" color="textSecondary">Total Amount</Typography>
                      <Typography variant="h5" fontWeight="bold">
                        LKR {totalAmount.toFixed(2)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Amount Paid"
                      type="number"
                      value={payment.amountPaid}
                      onChange={(e) => handlePaymentChange('amountPaid', e.target.value)}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">LKR</InputAdornment>,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Change"
                      value={payment.change > 0 ? payment.change.toFixed(2) : '0.00'}
                      InputProps={{
                        readOnly: true,
                        startAdornment: <InputAdornment position="start">LKR</InputAdornment>,
                      }}
                    />
                  </Grid>
                </>
              )}

              {payment.method === 'cheque' && (
                <Grid item xs={12}>
                  <Alert severity="info" icon={<ChequeIcon />}>
                    <Typography variant="body2" fontWeight="bold">
                      Cheque Payment Selected
                    </Typography>
                    <Typography variant="body2">
                      Amount: LKR {totalAmount.toFixed(2)}
                    </Typography>
                    <Typography variant="body2">
                      You will enter cheque details on the next screen.
                    </Typography>
                  </Alert>
                </Grid>
              )}

              {payment.method === 'credit' && selectedCustomer.is_credit_customer && (
                <Grid item xs={12}>
                  <Alert severity="info" icon={<CreditCardIcon />}>
                    <Typography variant="body2" fontWeight="bold">
                      Credit Payment Selected
                    </Typography>
                    <Typography variant="body2">
                      Amount: LKR {totalAmount.toFixed(2)}
                    </Typography>
                    <Typography variant="body2">
                      Available Credit: {formatCurrency(selectedCustomer.available_credit)}
                    </Typography>
                  </Alert>
                </Grid>
              )}
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPaymentDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              if (payment.method === 'credit' && selectedCustomer.is_credit_customer) {
                setCreditPaymentOpen(true);
              } else if (payment.method === 'cheque') {
                setChequePaymentOpen(true);
              } else {
                processPayment();
              }
            }}
            disabled={loading || (payment.method !== 'credit' && payment.method !== 'cheque' && parseFloat(payment.amountPaid || 0) < totalAmount)}
          >
            {payment.method === 'credit' ? 'Process Credit' :
             payment.method === 'cheque' ? 'Enter Cheque Details' :
             'Complete Transaction'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* New Customer Dialog */}
      <Dialog open={customerDialogOpen} onClose={() => setCustomerDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Customer</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Customer Name *"
                  value={newCustomer.name}
                  onChange={(e) => setNewCustomer(prev => ({ ...prev, name: e.target.value }))}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Customer Type"
                  select
                  value={newCustomer.customer_type}
                  onChange={(e) => setNewCustomer(prev => ({ ...prev, customer_type: e.target.value }))}
                >
                  <MenuItem value="individual">Individual</MenuItem>
                  <MenuItem value="corporate">Corporate</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Phone Number *"
                  value={newCustomer.phone}
                  onChange={(e) => setNewCustomer(prev => ({ ...prev, phone: e.target.value }))}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={newCustomer.email}
                  onChange={(e) => setNewCustomer(prev => ({ ...prev, email: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address"
                  multiline
                  rows={2}
                  value={newCustomer.address}
                  onChange={(e) => setNewCustomer(prev => ({ ...prev, address: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Credit Limit (LKR)"
                  type="number"
                  value={newCustomer.credit_limit}
                  onChange={(e) => setNewCustomer(prev => ({ ...prev, credit_limit: e.target.value }))}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">LKR</InputAdornment>,
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCustomerDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateCustomer}
            disabled={!newCustomer.name.trim() || !newCustomer.phone.trim()}
          >
            Add Customer
          </Button>
        </DialogActions>
      </Dialog>

      <EditItemDialog />
      <BulkSaleDialog />
    </Container>
  );
};

export default POS;